// Las Vegas, "Fountains on the Strip": a warm, clear October night on the Bellagio's lakeside walk, as a postcard.
// Back to front: an indigo desert sky warming to rose over the Strip, a big moon, thin clouds and stars; Frenchman
// Mountain low and dark; the Strat's needle far up the Strip; Caesars Palace's towers with their blue crowns; the
// Bellagio's curved tower at the left; the High Roller and the Sphere (showing the Earth); a Welcome-style diamond
// casino sign on the Strip (the exported `billboard`); the half-scale Eiffel Tower of Paris, floodlit gold (the hero),
// over the Paris casino front, with its blue balloon sign; the Strip's traffic, palms and lamps along the far shore; the
// Bellagio lake with its fountains and reflections; the lakeside balustrade with lamps and people stopping to watch;
// and our side of the walk: a little white wedding chapel with a steeple, a neon heart, a red carpet, potted palms
// and a JUST MARRIED convertible, palms lining the walk and a bed of flowers along the front.
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, env, inEvent, cycle, hash, rng, bayer, hex,
  makeCanvas, px, rect, ditherFill, makeHalo, drawHalo, glyph, FONT3, reflect, streak, makePitch, gradeAd } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                          // this scene's 480x270 context, set by create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= Layout ================= */
const HZ = 162;                     // the Strip's far sidewalk: the lake's far edge (facades end on row 159)
const LAKE = 166;                   // top of the lake (it runs on behind our balustrade to FLOOR)
const RAIL = 196;                   // top of our balustrade's rail
const FLOOR = 208;                  // first row of our walk: people at the rail stand on row FLOOR - 1
const EF = { x: 284, base: 160, tip: 30 };     // Paris Eiffel Tower: centre column, foot row, beacon row
const BAL = { x: 326, top: 124 };              // the balloon sign: centre column, top of the envelope
const HRW = { x: 128, y: 128, r: 30 };         // High Roller: hub and rim radius
const SPH = { x: 168, y: 144, r: 20 };         // Sphere: centre and radius (it stands cut off at its base)
const STRAT = { x: 93, tip: 76, ground: 162 }; // the Strat's mast tip and (hidden) foot
const BB = { x: 206, y: 124, w: 44, h: 18 };   // billboard face, centred in the Welcome-style diamond
const DIA = { x: BB.x + 22, y: BB.y + 9, a: 36, b: 25 };   // the diamond: centre and half-diagonals
const MOON = { x: 178, y: 40, r: 8 };
const CH = { x0: 388, x1: 460, cx: 424, eave: 150, base: 210 };   // chapel walls, eave row, porch floor row
const LAMPS = [150, 238];                  // lamp posts on the balustrade's pedestals (x)
const NEAR_LAMP = { x: 22, foot: 262, head: 160 };   // a tall five-globe lamp on our walk, framing the left

/* ================= Pixel helpers ================= */
function painter(w, h) {
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data, cache = {};
  const put = (i, y, col, a = 255) => {
    i = Math.round(i); y = Math.round(y);
    if (i < 0 || y < 0 || i >= w || y >= h) return;
    const rgb = cache[col] || (cache[col] = hex(col)), o = (y * w + i) * 4;
    d[o] = rgb[0]; d[o + 1] = rgb[1]; d[o + 2] = rgb[2]; d[o + 3] = a;
  };
  const alpha = (i, y) => (i < 0 || y < 0 || i >= w || y >= h) ? 0 : d[(y * w + i) * 4 + 3];
  return { c, x, put, alpha, done: () => { x.putImageData(img, 0, 0); return c; } };
}
function line(c, x0, y0, x1, y1, col) {
  x0 = Math.round(x0); y0 = Math.round(y0); x1 = Math.round(x1); y1 = Math.round(y1);
  const dx = Math.abs(x1 - x0), dy = -Math.abs(y1 - y0), sx = x0 < x1 ? 1 : -1, sy = y0 < y1 ? 1 : -1;
  let err = dx + dy;
  c.fillStyle = col;
  for (;;) {
    c.fillRect(x0, y0, 1, 1);
    if (x0 === x1 && y0 === y1) break;
    const e2 = 2 * err;
    if (e2 >= dy) { err += dy; x0 += sx; }
    if (e2 <= dx) { err += dx; y0 += sy; }
  }
}
function sprite(rows, key, flip = false) {
  const h = rows.length, w = Math.max(...rows.map(r => r.length)), p = painter(w, h);
  rows.forEach((row, y) => { for (let i = 0; i < row.length; i++) { const col = key[row[i]]; if (col) p.put(flip ? w - 1 - i : i, y, col); } });
  return p.done();
}
const tintCache = {};
function tint(col, tcol, a) {                    // #rrggbb part-way towards another colour
  const k = col + tcol + a;
  if (!tintCache[k]) {
    const A = hex(col), B = hex(tcol);
    tintCache[k] = '#' + [0, 1, 2].map(j => Math.round(A[j] + (B[j] - A[j]) * a).toString(16).padStart(2, '0')).join('');
  }
  return tintCache[k];
}
// sign lettering: FONT3, with M, N and W five and four pixels wide and a K that can't pass for an H
const WIDE = {
  M: ['10001', '11011', '10101', '10001', '10001'], N: ['1001', '1101', '1011', '1001', '1001'],
  W: ['10001', '10001', '10101', '11011', '10001'], K: ['1001', '1010', '1100', '1010', '1001'],
};
const glyphOf = ch => WIDE[ch] || FONT3[ch];
function signWidth(s, gap = 1) { let w = 0; for (const ch of s.toUpperCase()) w += (glyphOf(ch) ? glyphOf(ch)[0].length : 3) + gap; return w - gap; }
function sign(c, s, x, y, col, gap = 1) {
  let cx = Math.round(x);
  for (const ch of s.toUpperCase()) { const g = glyphOf(ch); if (g) glyph(c, g, cx, Math.round(y), col); cx += (g ? g[0].length : 3) + gap; }
}

/* ================= Sky: indigo overhead, warming to rose over the Strip ================= */
const SKY = ['#06071a', '#080a21', '#0b0c28', '#0e0f2f', '#121236', '#16153d', '#1b1843', '#211b49', '#281e4e',
  '#302153', '#392457', '#43275a', '#4e2a5c', '#5a2e5d', '#67325d', '#75375c', '#843d5a'];
function renderSky() {
  return ditherFill(W, H, SKY, (i, y) => {
    let v = Math.pow(Math.min(1, y / 166), 1.8) * 0.86;
    v += Math.max(0, 1 - Math.hypot((i - 236) / 320, (y - 172) / 74)) * 0.2;   // the Strip's glow
    return v;
  });
}
function renderMoon() {
  const r = MOON.r, s = r * 2 + 1, p = painter(s, s);
  const maria = [[-3, -2, 2.2], [2, 1, 1.8], [-1, 4, 1.4], [3, -4, 1.1], [-5, 2, 1]];
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const dx = i - r, dy = y - r, dd = Math.hypot(dx, dy);
    if (dd > r + 0.35) continue;
    let col = dx + dy < -6 ? '#fffbee' : '#f4ecd6';
    if (dd > r - 1.2) col = dx + dy > 0 ? '#c9bc9c' : '#e6dcc0';
    for (const [cx, cy, cr] of maria) if (Math.hypot(dx - cx, dy - cy) < cr) col = dd > r - 1.2 ? '#bfb192' : '#ddd0b2';
    p.put(i, y, col);
  }
  return p.done();
}
const STARS = [];
{
  const r = rng(29);
  while (STARS.length < 34) {
    const x = Math.floor(r() * W), y = 3 + Math.floor(r() * 104);
    if (Math.hypot(x - MOON.x, y - MOON.y) < 22 || (x > 250 && x < 318 && y > 24)) continue;
    STARS.push({ x, y, k: 12 + Math.floor(r() * 50), ph: r(), b: 0.25 + r() * 0.55 });
  }
}

/* ================= Thin desert clouds (tileable; moonlit tops, rose-lit bellies) ================= */
const CLOUD_DEFS = [
  { seed: 5, y: 40, h: 30, banks: 2, k: 1, pal: ['#1e1b44', '#2a2554', '#3a3266', '#4c3a6c'], belly: '#56345e' },
  { seed: 23, y: 74, h: 32, banks: 3, k: 2, pal: ['#231d4a', '#2f2658', '#3e2f66', '#52386c'], belly: '#6a3a62' },
];
function makeCloudLayer(def) {
  const r = rng(def.seed), h = def.h, puffs = [];
  for (let b = 0; b < def.banks; b++) {
    const bx = r() * W, bw = 70 + r() * 90, base = h * (0.62 + r() * 0.15);
    const np = 5 + Math.floor(r() * 4);
    for (let i = 0; i < np; i++) {
      const rx = 14 + r() * 22, ry = 3.5 + r() * 4.5;
      puffs.push({ x: bx + (r() - 0.5) * bw, y: base - r() * ry * 0.9, rx, ry, base });
    }
  }
  const p = painter(W, h);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    let best = -9, top = 0;
    for (const q of puffs) {
      let dx = x - q.x; dx -= Math.round(dx / W) * W;
      const dy = y - q.y;
      let v = 1 - (dx * dx) / (q.rx * q.rx) - (dy * dy) / (q.ry * q.ry);
      if (y > q.base) v -= (y - q.base) * 0.5;                          // flat bellies
      if (v > best) { best = v; top = -dy / q.ry; }
    }
    const dens = clamp(best * 1.6, 0, 1);
    if (dens <= bayer(x, y) * 0.9) continue;                             // soft, dithered edges
    const lightness = clamp(0.35 + top * 0.45 + dens * 0.25, 0, 0.999);   // moonlit tops, darker cores
    let col = def.pal[Math.floor(lightness * def.pal.length)];
    if (top < -0.55 && dens > 0.3 && bayer(x, y) < 0.6) col = def.belly;  // bellies lit by the Strip
    p.put(x, y, col);
  }
  return { c: p.done(), y: def.y, k: def.k };
}

/* ================= Frenchman Mountain, low and dark beyond the Strip ================= */
function ridgeY(x) {
  const peak = (c, w, hgt) => hgt * Math.exp(-(((x - c) / w) ** 2));
  return Math.round(156 - peak(338, 46, 16) - peak(262, 40, 7) - peak(150, 60, 5) - peak(30, 50, 4)
    - 1.2 * Math.sin(x * 0.21) - 0.8 * Math.sin(x * 0.53 + 1));
}
function renderMountains() {
  const p = painter(W, H);
  for (let i = 0; i < W; i++) {
    const top = ridgeY(i);
    for (let y = top; y < HZ; y++) {
      const v = (y - top) / 14;
      let col = y === top ? '#3a2c52' : v + (bayer(i, y) - 0.5) * 0.5 < 0.6 ? '#1d1734' : '#271c3c';
      if (y > 150 && bayer(i, y) < (y - 150) / 14) col = '#3a2444';            // the city's haze at its foot
      p.put(i, y, col);
    }
  }
  return p.done();
}
/* ================= Far towers: the Strat, Caesars Palace, the Bellagio ================= */
const WIN_W = ['#ffe2a6', '#f6c97c', '#e8ab5e'], WIN_C = ['#dceaff', '#b4ccf0'];
// tidy hotel window rows: every other column, every `dy` rows, a share `p` of them lit (mostly warm)
function windows(put, r, x0, x1, y0, y1, dy, p, dark, warm = 0.75) {
  for (let y = y0; y <= y1; y += dy) for (let i = x0; i <= x1; i += 2) {
    const v = r();
    if (v < p) put(i, y, r() < warm ? WIN_W[Math.floor(r() * 3)] : WIN_C[Math.floor(r() * 2)]);
    else put(i, y, dark);
  }
}
function drawStrat(put) {
  const { x: cx, tip, ground } = STRAT, h = ground - tip;
  const podBot = Math.round(ground - 0.68 * h), podTop = podBot - 8;
  for (let y = podBot + 1; y < ground; y++) {                       // the tapering concrete shaft, lit from the Strip
    const w = y > ground - 34 ? 4 : 3, x0 = cx - (w >> 1);
    for (let k = 0; k < w; k++) put(x0 + k, y, k === 0 ? '#c6cce6' : k === w - 1 ? '#3e4262' : '#7c84a8');
  }
  const ring = (y, hw, col) => { for (let i = -hw; i <= hw; i++) put(cx + i, y, typeof col === 'function' ? col(i) : col); };
  ring(podBot, 3, '#3a3c58'); ring(podBot - 1, 4, '#5c5e7c');
  for (const y of [podBot - 2, podBot - 3]) ring(y, 5, i => ((i + y) & 1) ? '#ffe0a0' : '#2c2e48');   // restaurant ring
  ring(podBot - 4, 5, '#9a9cba'); ring(podBot - 5, 5, i => (i & 1) ? '#c8b4ff' : '#3a3058');           // the lit deck
  ring(podBot - 6, 4, '#8a8cac'); ring(podBot - 7, 3, '#6a6c8c'); ring(podTop, 1, '#4a4c68');
  for (let y = tip + 1; y < podTop; y++) put(cx, y, y > podTop - 7 ? '#8a8cac' : '#5a5c7c');               // the mast and its ride
  put(cx - 1, podTop - 3, '#5a5c7c'); put(cx + 1, podTop - 3, '#5a5c7c');
}
function drawCaesars(put, r) {
  // Palace Tower behind (flat top, two setbacks), Augustus Tower in front (its roof a shallow arch); blue-lit crowns
  const slab = (x0, x1, top, arch) => {
    for (let i = x0; i <= x1; i++) {
      const u = (i - x0) / (x1 - x0), yt = top + (arch ? Math.round(3 * (2 * u - 1) ** 2) : 0);
      for (let y = yt; y < 152; y++) put(i, y, i === x0 ? '#8e90ae' : i === x1 ? '#2a2a40' : u < 0.5 ? '#4a4a66' : '#3c3c56');
      put(i, yt, '#8ff0ff'); put(i, yt + 1, '#3ab8e0');                                   // the crown's blue light
      if (!arch && i > x0 + 1 && i < x1 - 1) put(i, yt + 6, '#5ad0f0');
    }
    windows(put, r, x0 + 2, x1 - 2, top + (arch ? 6 : 9), 150, 3, 0.42, '#2a2a40');
  };
  slab(62, 88, 90, false);
  for (let i = 65; i <= 85; i++) { put(i, 86, '#8ff0ff'); for (let y = 87; y < 90; y++) put(i, y, i === 65 ? '#8e90ae' : '#3c3c56'); }   // setback
  slab(54, 76, 102, true);
}
// the Bellagio's curved tower, seen along its curve from the lake: its near end at the frame's edge, its floors bowing
// away to the right, the stepped crown with little pitched roofs
const BEL = { x1: 60, top0: 62, top1: 82, foot: 150 };
const belTop = i => Math.round(BEL.top0 + (BEL.top1 - BEL.top0) * Math.pow(i / BEL.x1, 1.4));
function drawBellagio(put, r) {
  const sc = i => 1 - 0.32 * Math.pow(i / BEL.x1, 1.3);                          // the curve turning away: floors close up
  for (let i = 0; i <= BEL.x1; i++) {
    const u = i / BEL.x1, top = belTop(i), near = i < 30;
    const winCol = near ? i % 3 !== 0 : i % 2 === 1, k = near ? Math.floor(i / 3) : 10 + (i >> 1);
    for (let y = top + 2; y < BEL.foot; y++) put(i, y, u < 0.45 ? '#a49aa8' : '#8a8296');
    if (winCol) for (let fl = 0; ; fl++) {
      const y = Math.round(BEL.foot - (fl + 0.5) * 3 * sc(i));
      if (y <= top + 2) break;
      const v = hash(k, fl, 7);
      put(i, y, v < 0.42 ? WIN_W[Math.floor(hash(k, fl, 8) * 3)] : v < 0.52 ? '#9a7458' : '#3a3046');
    }
    if (i === BEL.x1) for (let y = top; y < BEL.foot; y++) put(i, y, '#2e2838');
    put(i, top, '#e2d6d0'); put(i, top + 1, '#bfb2b4');
  }
  // the crown: two flat-topped pavilions stepping up over the near end, lit cornices, terracotta roofs, arched windows
  const pavilion = (x0, x1, top) => {
    for (let i = x0; i <= x1; i++) {
      for (let y = top; y < belTop(i); y++) {
        let col = i === x0 ? '#d6cac6' : i === x1 ? '#5a5060' : '#aea2ac';
        if (y === top) col = '#f2e6da';
        else if (y >= top + 2 && y <= top + 4 && (i - x0) % 4 === 2) col = y === top + 2 ? '#e8b878' : '#ffd796';   // arched windows
        put(i, y, col);
      }
      const ridge = Math.round(2.5 * (1 - Math.abs((i - x0) / (x1 - x0) * 2 - 1)));
      for (let k = 1; k <= ridge + 1; k++) put(i, top - k, k === ridge + 1 ? '#d88a5a' : '#9a4e36');
    }
  };
  pavilion(3, 38, belTop(3) - 7);
  pavilion(11, 27, belTop(3) - 14);
}
// low frontage along the lake's left shore: the Bellagio's arcades and the casino podiums beyond
function drawFrontLeft(put, r) {
  for (let i = 0; i < 196; i++) {
    const top = i < 100 ? 148 : 150 + Math.round(2 * Math.sin(i * 0.11));
    for (let y = top; y < 160; y++) {
      let col = i < 100 ? '#8e7a64' : '#2e2440';
      if (i < 100 && y > top + 2 && y < 158 && (i % 5) < 3 && ((y - top) % 5) > 1) col = '#ffcf86';   // lit arched bays
      if (i >= 100 && y > top + 1 && (y - top) % 3 === 0 && (i & 1) && hash(i, y, 11) < 0.5) col = hash(i, y, 12) < 0.7 ? '#f0c27a' : '#c8dcff';
      put(i, y, col);
    }
    put(i, top, i < 100 ? '#e8d4b0' : '#5a4a6a');
  }
  for (let i = 112; i < 150; i += 2) put(i, 151, hash(i, 1, 13) < 0.85 ? '#ffe9a8' : '#8a6a3a');      // a marquee's bulbs
}
function renderFar() {
  const p = painter(W, H), r = rng(41);
  drawStrat(p.put);
  drawCaesars(p.put, r);
  drawBellagio(p.put, r);
  drawFrontLeft(p.put, r);
  return p.done();
}

/* ================= High Roller: legs, rim, spokes, hub (the cabins are drawn per frame) ================= */
const CABS = 28;
function renderWheel() {
  const { x: cx, y: cy, r: R } = HRW, p = painter(W, H);
  const leg = (fx) => {                                                            // A-frame legs from the hub
    const n = HZ - cy;
    for (let k = 0; k <= n; k++) {
      const x = Math.round(cx + (fx - cx) * k / n), y = cy + k;
      p.put(x - 1, y, '#5a5478'); p.put(x, y, '#2e2a44'); p.put(x + 1, y, '#24203a');
    }
  };
  leg(cx - 20); leg(cx + 20);
  for (let i = cx - 12; i <= cx + 12; i++) p.put(i, cy + 20, '#3a3654');               // the brace between them
  for (let s = 0; s < CABS; s++) {                                                  // cable spokes, faint
    const a = s * TAU / CABS;
    for (let k = 4; k < R - 1; k++) if ((k + s) % 2 === 0) p.put(cx + Math.sin(a) * k, cy - Math.cos(a) * k, '#4a3a66');
  }
  for (let dy = -3; dy <= 3; dy++) for (let dx = -3; dx <= 3; dx++) {
    const d = Math.hypot(dx, dy);
    if (d < 3.3) p.put(cx + dx, cy + dy, d < 1.2 ? '#fff4ff' : d < 2.3 ? '#d6c8ee' : '#6a5c8a');
  }
  return p.done();
}
const CAB_C = ['.gg.', 'gGGg', 'gGGg', '.gg.'];
function drawCabins(t) {
  const { x: cx, y: cy, r: R } = HRW, rot = TAU / CABS * (t / LOOP);             // one cabin spacing per loop
  for (let s = 0; s < CABS; s++) {
    const a = s * TAU / CABS + rot, x = Math.round(cx + Math.sin(a) * (R + 3)) - 1, y = Math.round(cy - Math.cos(a) * (R + 3)) - 1;
    CAB_C.forEach((row, j) => { for (let i = 0; i < 4; i++) if (row[i] !== '.') px(ctx, x + i, y + j, row[i] === 'G' ? (s % 7 === 3 ? '#ffe6b0' : '#bfe0ff') : '#3e4a6e'); });
  }
}

/* ================= The Sphere, showing the Earth ================= */
// the world at 2 degrees a cell, from Natural Earth's 1:110m land (as on the city picker's globe): rows from 90N,
// columns from 180W, each a run of cells (O ocean, G land, S sand, I ice, then how many in base 36)
const WORLD =
  'O50|O50|O50|O1dI9O9IaO2v|O17I14O1iI2O15|O17I4G5O3G2InOgG4O11I3G2O12|OuG1ObG1O3G5O4I1G1InO17G1OhG2O11|' +
  'OwG3O1G1O3G1O1G1O1G6ObIjO12G2OeGcOdG3Oi|OsG6O2G1O2G2O1G2O1G5OcIhO11G2O7G1O4GoO6G2Oi|' +
  'ObG1O1G1OhG7O4G2O1GaO8IgOnG3OcG1O6G2O1G16Ob|O7GvO3G1O1G2O2G2O3G4O8IdOmG9OcG3O1G1k|G4O4G15O4G6O4IaOoGdO2GeO1G1h|' +
  'O2G2O3G13O1G1O3G6O7I6O9G4OdG5O1G6O2G1y|O8G10OaG3O8I4OpG5O2G26O1|O7G10O8G4ObI3OnG7O2G1vO2G6O4|' +
  'O9G4O7GnO8G5O1G1OzG6O3G1oO4G1O3G1O9|OnGmO7G7OsG2O5G1O1G2O3G1mO9G3O9|OoGoO3GaOpG1O1G1O5G1O5G1mOaG3O9|' +
  'OqGnO2GbOnG2O1G2O2G1yO6G2Oa|OqG10OqG2O1G1xO1G1Oi|OrGuO4G2OqG1zO1G1Oi|OsGuOvG1yOl|OsGvOuGgO2G1O1GcS1GkS3GdOm|' +
  'OsGrOvG6O3G2O2G5O6G7S6GeS9GaO4G2Oi|OsGqOvG6O3G1O2G1O2GiS5G6S3G1S1G1S8G2S1G9O5G1Oj|' +
  'OsGpOwG5OaG2O1GfS1GaSaGaO3G2O6G1Oj|OtG2S4GhOyG3O4G2O9GqS3G2S3GdO2G2O3G2Ok|OuS6GgOzG8OdGbS2GtO3G1O2G4Ok|' +
  'OvS6GdO10GaOcG9S5GsO5G1Oo|OwS5GcO10G6S1G2S3G2O1G2S5G2S3G4S6GtOt|OyG1S3G4O6G2OzStO1G2S6G3S1GoOt|' +
  'OxG1O1S3G3O8G1OxSoO1S7O2G4S1G2S3GnOu|OyG1O1G5O15SoG1O1S8O1G1O3G1O1G2S1GnOv|O11G4O8G1OwSoG2O1S9G2O4GoO2G1Ot|' +
  'O11G4O4G1O5G1OuSpG2O1S9O6G9O2G8O10|O12G4O2G2O7G2OrSqG1O1S9O7G6O5G6O1G1Oz|O14G6O10SoG3O2S6OaG4O6G7O6G1Ot|' +
  'O18G4OxGaS1G2S3G3S3G1S2G4O1G1S3OcG3O9G5O6G1Ot|O1aG2OyGvOeG3O9G6Oz|O1bG1O5G3OqGuO1G2OdG2O9G1O2G2O10|' +
  'O1cG1O3G8OnGwOdG1OaG1OcG1Or|O1fGaOnGvOfG1O9G1ObG1Or|O1fGdOlG4O4GlOoG1O1G2O5G2Ov|O1fGeOuGiOqG1O1G1O4G3Ov|' +
  'O1eGfOuGhOrG3O3G4Ov|O1eGhOrGhOtG2O3G4O1G1O5G1On|O1eGkOpGfOvG2O2G3O2G1O5G5Oj|O1dGnOoGeOwG1OfG5Oh|' +
  'O1eGnOnGeOxG1O1G1OdG5Og|O1fGlOoGeO1hG2Of|O1fGlOpGdO1y|O1gGjOpGeO4G1O14G3O3G1Oi|O1gGjOpGeO4G1O11G6O3G2Oh|' +
  'O1iGhOpGdO3G3O10G8O1G3Oh|O1jGfOqS4G8O4G3O10S6G6Oh|O1jS1GeOrS5G6O4G2OySaG7Of|O1jGeOsS5G6O4G2OxG1SdG4Of|' +
  'O1jGbOvS6G4O5G1OyG1SeG4Oe|O1iGcOwG1S1G1S1G4O15G2SdG5Od|O1iGbOxG8O15G3SbG6Od|O1iGbOyG6O17G7S4G8Od|' +
  'O1iGaOzG5O18G4O5S1G8Oe|O1iG7O2fG1OaG6Of|O1hG8O2rG5OcG1O2|O1hG3S1G3O39G2O1|O1hG3S2O2xG1OcG1O3|O1hG2S3O2xG1ObG2O3|' +
  'O1hS4O39G2O4|O1hG1S3O3f|O1gG2S3O3f|O1gG3S1O3g|O1hG3O3g|O1jG2O3f|O50|O50|O50|O50|O1mI1O3d|' +
  'O1kI2O1lI4OeI4O2IaO1IdOh|O1kI2O1dI1O2IfO4I13Oc|O1hI6OwIxO2I1cO6|O13I2O4I2O7I6OnI2kO5|OjI13OnI2kO7|' +
  'OcI1O2I12OmI2pO8|O9I1O2I13OeI3O4I2sO8|OdI1O1I14O9I2O6I2qOa|OdI4hO6|IaO2I2O1I4l|I50|I50';
const decodeWorld = () => {
  const m = new Uint8Array(180 * 90);
  WORLD.split('|').forEach((row, j) => {
    let i = j * 180;
    for (const [, v, n] of row.matchAll(/([OGSI])([0-9a-z]+)/g)) { const len = parseInt(n, 36); m.fill('OGSI'.indexOf(v), i, i + len); i += len; }
  });
  return m;
};
// the light on the Sphere's picture, as in a photo of the Earth from space: the sun high at the left and a little in
// front, so a crescent of night shows on the lower right; GLINT is halfway between the sun and us (the sun's glint)
const SUN = [-0.55, 0.42, 0.72], GLINT = [SUN[0], SUN[1], SUN[2] + 1].map((v, _, a) => v / Math.hypot(...a));
const dot3 = (a, x, y, z) => a[0] * x + a[1] * y + a[2] * z;
// brightness 0..1 at a point of the orb: Lambert with a soft terminator, and a limb that darkens as it turns away
const sunlit = (nx, ny, nz) => clamp(dot3(SUN, nx, ny, nz) * 1.15 + 0.05, 0, 1) ** 0.8 * (0.5 + 0.5 * Math.sqrt(nz));
// a tone from a ramp (night to full sun), ordered-dithered between its steps
const tone = (ramp, v, i, y) => ramp[clamp(Math.floor(v * (ramp.length - 1) + bayer(i, y)), 0, ramp.length - 1)];
const OCEAN_T = ['#050a1c', '#0b1a44', '#12306e', '#1a4594', '#2158b2', '#2a6ac8', '#357edc'];
const LAND_T = ['#060c08', '#102014', '#1e3820', '#2c4e2a', '#3a6232', '#4a743a', '#5c8644'];
const SAND_T = ['#0e0a06', '#2a1e10', '#4e3a1e', '#6e5428', '#8a6c34', '#a48440', '#bc9a50'];
const ICE_T = ['#0c1226', '#2c3a5c', '#5a6c94', '#8a9cc0', '#aebedc', '#cad8ee', '#e6eefa'];
const CLOUD_T = ['#0c1226', '#2e3a5a', '#5e6e94', '#909fc2', '#b6c4e0', '#d4def2', '#f2f6ff'];
const ATMO_T = ['#12286a', '#1c48b0', '#2a6ae0', '#3c8cff', '#5eaaff'];     // the atmosphere along the lit limb
const SURF_T = [OCEAN_T, LAND_T, SAND_T, ICE_T];
// smooth value noise, wrapping every `wrap` cells along x
function vnoise(x, y, wrap, seed) {
  const x0 = Math.floor(x), y0 = Math.floor(y), u = x - x0, v = y - y0, su = u * u * (3 - 2 * u), sv = v * v * (3 - 2 * v);
  const g = (a, b) => hash(((a % wrap) + wrap) % wrap, b & 255, seed);
  return lerp(lerp(g(x0, y0), g(x0 + 1, y0), su), lerp(g(x0, y0 + 1), g(x0 + 1, y0 + 1), su), sv);
}
// cloud cover 0..1: wisps drawn out along the latitude, leaning poleward to the east like fronts, thickest in the
// storm tracks round 50 degrees and along the equator, thin over the subtropics' deserts
function cloudAt(LON, LAT) {
  const a = Math.abs(LAT), x = (LON + 180 + a * 1.4) / 360;
  const n = 0.62 * vnoise(x * 14, LAT / 6, 14, 21) + 0.38 * vnoise(x * 34, LAT / 2.6, 34, 22);
  const band = 0.18 + 0.42 * Math.exp(-(((a - 50) / 13) ** 2)) + 0.3 * Math.exp(-(((LAT - 5) / 7) ** 2));
  return clamp((n * (0.6 + band) - 0.53) / 0.2, 0, 1);
}
// the Sphere's screen: a truncated orb; fn(nx, ny, nz, i, y) gives each lit pixel's colour (ny up, nz towards us)
function renderOrb(fn) {
  const R = SPH.r, s = R * 2 + 1, cut = Math.round(R * 0.42), p = painter(s, R + cut + 1);
  for (let y = 0; y <= R + cut; y++) for (let i = 0; i < s; i++) {
    const nx = (i - R) / (R + 0.4), ny = (R - y) / (R + 0.4), q = 1 - nx * nx - ny * ny;
    if (q < 0) continue;
    p.put(i, y, y >= R + cut ? '#15142a' : fn(nx, ny, Math.sqrt(q), i, y));       // the dark base ring
  }
  return p.done();
}
// the Earth turning, n frames from lon0 (radians) with lat0 tilted towards us: each pixel's 3x3 samples of the map are
// worked out once, so its coasts come out clean, then every frame only turns the map under them
function renderEarthFrames(n, lon0, lat0) {
  const map = decodeWorld(), R = SPH.r, c0 = Math.cos(lat0), s0 = Math.sin(lat0), geo = new Map(), DEG = 180 / Math.PI;
  const sample = (i, y) => {
    const nx = (i - R) / (R + 0.4), ny = (R - y) / (R + 0.4), q = 1 - nx * nx - ny * ny;
    if (q < 0) return null;
    const nz = Math.sqrt(q), lat = Math.asin(clamp(ny * c0 + nz * s0, -1, 1)) * DEG;
    return [clamp(Math.floor((90 - lat) / 2), 0, 89) * 180, Math.atan2(nx, nz * c0 - ny * s0) * DEG, lat];
  };
  const pixel = (i, y) => {
    const k = y * 64 + i;
    if (!geo.has(k)) {
      const subs = [];
      for (const b of [-1 / 3, 0, 1 / 3]) for (const a of [-1 / 3, 0, 1 / 3]) { const s = sample(i + a, y + b); if (s) subs.push(s); }
      geo.set(k, { subs, mid: sample(i, y) });
    }
    return geo.get(k);
  };
  const cellOf = (row, lon) => map[row + Math.floor((((lon + 180) % 360) + 360) % 360 / 2)];
  const out = (i, y) => (i - R) ** 2 + (R - y) ** 2 > (R + 0.4) ** 2;              // off the orb
  const frames = [];
  for (let f = 0; f < n; f++) {
    const L0 = (lon0 - f / n * TAU) * DEG;
    frames.push(renderOrb((nx, ny, nz, i, y) => {
      const { subs, mid } = pixel(i, y), cnt = [0, 0, 0, 0];
      for (const [row, dl] of subs) cnt[cellOf(row, L0 + dl)]++;
      const LON = (((L0 + mid[1] + 180) % 360) + 360) % 360 - 180, LAT = mid[2];
      // the commonest of land, sand and ice where land covers most of the pixel, else the sea
      let mat = 0;
      if (cnt[1] + cnt[2] + cnt[3] > subs.length / 2) { mat = 1; if (cnt[2] > cnt[mat]) mat = 2; if (cnt[3] > cnt[mat]) mat = 3; }
      if (mat === 0 && LAT > 72 + (bayer(i, y) - 0.5) * 6) mat = 3;               // the Arctic's sea ice, round the pole
      const d = dot3(SUN, nx, ny, nz), v = sunlit(nx, ny, nz);
      // the atmosphere: a thin bright rim on the lit limb's last pixels, and a blue haze over the ground just inside
      if (d > 0.04 && (out(i - 1, y) || out(i + 1, y) || out(i, y - 1) || out(i, y + 1))) return tone(ATMO_T, clamp(d / 0.6, 0, 1), i, y);
      const haze = d > 0.1 && (1 - Math.hypot(nx, ny)) * (R + 0.4) < 2.2;
      const cl = cloudAt(LON, LAT);
      if (cl > 0.55 + bayer(i, y) * 0.2) return tone(CLOUD_T, v, i, y);              // thick cloud, and thin wisps
      if (cl > 0.3 + bayer(i, y) * 0.1) return tone(CLOUD_T, v * 0.74, i, y);
      // the night side's cities, on the land past the terminator
      if (mat === 1 && d < -0.05 && hash(Math.floor(LON / 2.5) & 255, Math.floor(LAT / 2.5) & 255, 31) > 0.9) return hash(Math.floor(LON / 2.5) & 255, 7, 32) < 0.5 ? '#ffd27a' : '#b07c3c';
      if (haze) return tone(OCEAN_T, v, i, y);
      if (mat === 0) {                                                              // the sun's glint on the sea
        const g = Math.max(0, dot3(GLINT, nx, ny, nz)) ** 160;
        if (g > 0.6 + bayer(i, y) * 0.3) return '#d8efff';
        if (g > 0.15 + bayer(i, y) * 0.4) return '#7cb8ff';
      }
      return tone(SURF_T[mat], v, i, y);
    }));
  }
  return frames;
}
// a full moon: pale highlands, the dark seas where they lie on the real one's near side, bright young craters
const MARIA = [[-30, 30, 22], [20, 25, 16], [35, 5, 14], [-10, -10, 15], [-45, -5, 12], [55, 15, 8], [15, -25, 10]];
const MOON_T = ['#121218', '#2e2d32', '#56544f', '#807c72', '#a6a296', '#c6c2b6', '#e2ded2'];
const MARE_T = ['#0e0e14', '#24242a', '#3e3d3e', '#5c5a54', '#76726a', '#8c887f', '#a09c92'];
const renderMoonPic = () => renderOrb((nx, ny, nz, i, y) => {
  const LON = Math.atan2(nx, nz) * 180 / Math.PI, LAT = Math.asin(clamp(ny, -1, 1)) * 180 / Math.PI;
  let sea = 0;
  for (const [lo, la, r] of MARIA) sea = Math.max(sea, 1 - Math.hypot(LON - lo, LAT - la) / r);
  const cx = (LON + 90) / 7, cy = (LAT + 90) / 7, v = sunlit(nx, ny, nz);
  const crater = hash(Math.floor(cx), Math.floor(cy), 13) > 0.9 && Math.hypot(frac(cx) - 0.5, frac(cy) - 0.5) < 0.3;
  if (crater && sea < 0.2 && v > 0.45) return '#fbf8f0';
  return tone(sea > 0.15 + bayer(i, y) * 0.3 ? MARE_T : MOON_T, v, i, y);
});
// October: a jack-o'-lantern, ribbed orange, its carved face lit from inside (lit 0..2: the candle's flicker)
const PUMPKIN_T = ['#160804', '#341406', '#58220a', '#80340e', '#a84a14', '#cc641c', '#ea862e', '#ffb04e'];
const STEM_T = ['#0c140a', '#1e3016', '#365424', '#527434'];
function renderPumpkin(lit) {
  const IN = [['#ffe27a', '#ffb83a'], ['#fff09a', '#ffc84a'], ['#fffac0', '#ffd65a']][lit];
  return renderOrb((nx, ny, nz, i, y) => {
    // the face sits high on the orb, where the Sphere shows above its base: eyes, a nose and a toothy grin
    const eye = cx => ny > 0.4 && ny < 0.64 && Math.abs(nx - cx) < 0.2 * (0.64 - ny) / 0.24 + 0.02;
    const nose = ny > 0.27 && ny < 0.38 && Math.abs(nx) < 0.09 * (0.38 - ny) / 0.11 + 0.01;
    const mc = 0.12 + 0.3 * nx * nx, mouth = Math.abs(nx) < 0.58 && Math.abs(ny - mc) < 0.07 && !(ny > mc + 0.01 && Math.abs(Math.abs(nx) - 0.22) < 0.06) && !(ny < mc - 0.02 && Math.abs(nx) < 0.05);
    if (eye(-0.36) || eye(0.36) || nose || mouth) return Math.hypot(nx, ny - 0.35) < 0.3 ? IN[0] : IN[1];
    const v = sunlit(nx, ny, nz);
    if (ny > 0.9 && Math.abs(nx) < 0.12) return tone(STEM_T, v, i, y);              // the stem
    // its ribs: narrow grooves between rounded lobes, and a soft shine where the sun catches it
    const groove = Math.abs(Math.sin(Math.atan2(nx, nz) * 5)) ** 4, shine = Math.max(0, dot3(GLINT, nx, ny, nz)) ** 30;
    return tone(PUMPKIN_T, v * (0.92 - 0.3 * groove) + shine * 0.3, i, y);
  });
}
// what the Sphere shows when: the Earth turning, a full moon, then October's jack-o'-lantern; 6 s dissolves between
const SPH_PROG = [['earth', 0, 56], ['moon', 62, 114], ['pumpkin', 120, 204], ['earth', 210, 240]];
const SPH_GLOW = { earth: '#4a8cff', moon: '#d8d8f0', pumpkin: '#ff8a2a' };
function spherePics(t) {
  const pic = k => k === 'earth' ? EARTH_F[Math.floor(frac(3 * t / LOOP) * EARTH_F.length)] : k === 'moon' ? MOONPIC_C : PUMPKIN_C[hash(7, step(t, 960)) < 0.2 ? 0 : hash(8, step(t, 960)) < 0.3 ? 2 : 1];
  for (let i = 0; i < SPH_PROG.length; i++) {
    const [k, a, b] = SPH_PROG[i];
    if (t >= a && t < b) return [[k, pic(k), 1]];
    const nx = SPH_PROG[i + 1];
    if (nx && t >= b && t < nx[1]) { const u = smooth(b, nx[1], t); return [[k, pic(k), 1 - u], [nx[0], pic(nx[0]), u]]; }
  }
  return [['earth', pic('earth'), 1]];
}
function drawSphere(t) {
  const pics = spherePics(t);
  for (const [k, , a] of pics) halo(HL['sph_' + k], SPH.x, SPH.y - 4, 0.9 * a);
  for (const [, c, a] of pics) { ctx.globalAlpha = a; ctx.drawImage(c, SPH.x - SPH.r, SPH.y - SPH.r); }
  ctx.globalAlpha = 1;
  return pics;
}

/* ================= The far shore: the Paris casino front, the Strip, the lake's far balustrade ================= */
const FAR_LAMPS = [], FAR_PALMS = [106, 140, 176, 352];
for (let x = 12; x < 372; x += 24) FAR_LAMPS.push(x);
function renderShore() {
  const p = painter(W, H), put = p.put;
  // Paris: a cream stone front with a slate mansard and lit dormers, arched windows, a taller pavilion under the tower
  for (let i = 196; i < 372; i++) {
    const pav = i >= 270 && i <= 298, roof = pav ? 142 : 146;
    for (let y = roof; y < 150; y++) {                                       // mansard
      const dorm = (i % 6 === 2) && y >= roof + 1 && y <= roof + 2;
      put(i, y, dorm ? '#ffe2a2' : y === roof ? '#7a7aa0' : ((i + y) & 3) === 0 ? '#45446a' : '#363458');
    }
    if (pav) for (let y = roof - 3; y < roof; y++) if (Math.abs(i - 284) <= 6 - (roof - y) * 2) put(i, y, y === roof - 3 ? '#9a9ac0' : '#4a4a70');
    put(i, 150, '#fbe8bc'); put(i, 151, '#c8a670');                          // cornice
    for (let y = 152; y < 160; y++) {
      const bay = (i - 196) % 6, arched = bay >= 2 && bay <= 3 && y >= 153 && y <= 158 && !(y === 153 && bay === 3);
      let col = (y - 152) / 8 + (bayer(i, y) - 0.5) * 0.4 < 0.5 ? '#e2c290' : '#c4a06c';
      if (bay === 0) col = '#f2dcae';
      if (arched) col = y === 155 ? '#c48a3e' : '#ffd27e';
      put(i, y, col);
    }
  }
  // the Strip: two rows of asphalt, then the far sidewalk's balustrade on the water
  for (let i = 0; i < 372; i++) {
    put(i, 160, '#1e1828'); put(i, 161, '#171320');
    put(i, HZ, '#e2d0ac'); put(i, HZ + 1, (i & 1) ? '#9a8a74' : '#3a3040'); put(i, HZ + 2, (i & 1) ? '#8a7a66' : '#2e2636');
    put(i, HZ + 3, '#5a4c4c');
  }
  for (const lx of FAR_LAMPS) { for (let y = 154; y < HZ; y++) put(lx, y, '#2a2232'); put(lx, 153, '#ffe6a8'); }
  // palms along the Strip: thin trunks, small crowns
  for (const fx of FAR_PALMS) {
    for (let y = 147; y < HZ; y++) put(fx + (y < 152 ? 1 : 0), y, '#3a2a2a');
    for (const [dx, dy] of [[-4, 1], [-3, 0], [-2, 0], [-1, -1], [0, -1], [1, -1], [2, -1], [3, 0], [4, 0], [5, 1], [-2, 1], [3, 1], [-4, 2], [5, 2], [1, 0], [0, 0]])
      put(fx + 1 + dx, 146 + dy, dy < 0 ? '#2e5a3a' : '#1c3a28');
  }
  return p.done();
}

/* ================= The billboard: a Welcome-style diamond sign on the Strip ================= */
// A diamond bordered by bulbs, a red eight-point star on top, CASINO in its lower point, two posts; the face is a cream
// panel across its middle with the casino's name: LUCKY on five silver dollars, JACKALOPE under it.
const inDia = (x, y) => Math.abs(x - DIA.x) / DIA.a + Math.abs(y - DIA.y) / DIA.b;
const BB_BULBS = [];
{
  for (let k = 0; k < 64; k++) {                                   // evenly round the diamond's edge, 1 px inside it
    const u = k / 64, q = Math.floor(u * 4), f = u * 4 - q, A = DIA.a - 2, B = DIA.b - 2;
    const P = [[DIA.x, DIA.y - B], [DIA.x + A, DIA.y], [DIA.x, DIA.y + B], [DIA.x - A, DIA.y], [DIA.x, DIA.y - B]];
    BB_BULBS.push([Math.round(lerp(P[q][0], P[q + 1][0], f)), Math.round(lerp(P[q][1], P[q + 1][1], f))]);
  }
}
const STAR = { x: DIA.x, y: DIA.y - DIA.b - 7 };
const SUITS = [['00100', '01110', '11111', '00100', '01110'], ['01010', '11111', '11111', '01110', '00100'],
  ['00100', '01110', '11111', '01110', '00100'], ['00100', '01110', '10101', '11111', '00100']];
function renderBillboard() {
  const p = painter(W, H), put = p.put;
  for (const lx of [DIA.x - 6, DIA.x + 5]) for (let y = DIA.y + DIA.b - 6; y < 160; y++) { put(lx, y, '#5a5a70'); put(lx + 1, y, '#2a2a3a'); }
  for (let y = DIA.y - DIA.b; y <= DIA.y + DIA.b; y++) for (let x = DIA.x - DIA.a; x <= DIA.x + DIA.a; x++) {
    const d = inDia(x, y);
    if (d > 1) continue;
    let col = y < DIA.y ? '#1e2f7a' : '#b8282a';                     // blue upper point, red lower point
    if (d > 0.9) col = '#f4ecd8';                                    // the white rim the bulbs sit on
    else if (d > 0.84) col = y < DIA.y ? '#0e1a4a' : '#6a1418';
    put(x, y, col);
  }
  for (let i = BB.x - 1; i <= BB.x + BB.w; i++) { put(i, BB.y - 1, '#f4ecd8'); put(i, BB.y + BB.h, '#f4ecd8'); }
  // the star: eight points, red with a gold rim, on a short stalk
  for (let y = -7; y <= 7; y++) for (let x = -7; x <= 7; x++) {
    const a = Math.atan2(y, x), rr = Math.hypot(x, y), lim = 3 + 3.6 * Math.pow(Math.abs(Math.cos(a * 4)), 3);
    if (rr <= lim) put(STAR.x + x, STAR.y + y, rr > lim - 1.1 ? '#ffd24a' : '#e2382c');
  }
  for (let y = STAR.y + 7; y < DIA.y - DIA.b; y++) put(STAR.x, y, '#8a8aa0');
  const c = p.done();
  sign(p.x, '777', DIA.x - Math.floor(signWidth('777') / 2), DIA.y + 12, '#ffd65a');
  SUITS.forEach((rows, k) => glyph(p.x, rows, DIA.x - 11 + k * 6, DIA.y - 16, k === 1 || k === 2 ? '#ff5a4a' : '#f4ecd8'));   // spade, heart, diamond, club
  return c;
}
const BB_NIGHT = { level: 0.78, tint: '#211b49', mix: 0.08, wear: 0.15, seed: 31 };
function renderBillboardAd() {
  const { w, h } = BB, p = painter(w, h);
  for (let y = 0; y < h; y++) for (let i = 0; i < w; i++) p.put(i, y, y >= h - 2 ? (y === h - 1 ? '#8e1e22' : '#c8302c') : y < 8 ? '#f6eedc' : '#efe4ce');
  const word = 'LUCKY', x0 = Math.round((w - (word.length * 8 - 1)) / 2);
  for (let k = 0; k < word.length; k++) {                          // silver dollars, a red letter on each
    const dx0 = x0 + k * 8;
    for (let y = 0; y < 7; y++) for (let i = 0; i < 7; i++) {
      const d = Math.hypot(i - 3, y - 3);
      if (d < 3.6) p.put(dx0 + i, 1 + y, d > 2.7 ? '#a49c8c' : '#ffffff');
    }
  }
  const c = p.done();
  for (let k = 0; k < word.length; k++) {
    const g = glyphOf(word[k]), gw = g[0].length;
    glyph(p.x, g, x0 + k * 8 + Math.floor((7 - gw) / 2), 2, '#d0302a');
  }
  sign(p.x, 'JACKALOPE', Math.round((w - signWidth('JACKALOPE')) / 2), 10, '#2448b8');
  return gradeAd(c, BB_NIGHT);
}
function drawBillboard(t, pitch) {
  halo(HL.bbWash, DIA.x, DIA.y, 0.8);
  ctx.drawImage(BB_C, 0, 0);
  if (!PITCH.covers(t, pitch)) ctx.drawImage(BB_AD, BB.x, BB.y);
  PITCH.draw(ctx, t, BB.x, BB.y, pitch);
  const s = step(t, 960);                                          // bulbs chase round the rim, four steps a second
  BB_BULBS.forEach(([x, y], k) => { px(ctx, x, y, (k + s) % 4 === 0 ? '#7a5a2a' : '#ffe08a'); });
  halo(HL.star, STAR.x, STAR.y, 0.7);
}

/* ================= Paris Las Vegas's Eiffel Tower (half scale, floodlit gold) ================= */
// After the real tower, scaled to EH = 130 px from foot to beacon: the base 51 px wide (330:125 = 2.64), the first
// platform at 0.175 of the height and 0.56 of the base wide, the second at 0.35 and 0.32, the third at 0.83; the legs'
// curve is the tower's own exponential profile. The big arch between the legs rises to the first platform.
const EH = EF.base - EF.tip, P1 = 23, P2 = 46, P3 = 109;
const G = { hi: '#fff3c8', l1: '#ffd97e', l2: '#f0b24a', m: '#c98632', s1: '#91591f', s2: '#5e3616', d: '#2e1a0e' };
function efHW(h) {
  if (h <= P1) return 25 * Math.exp(-0.0249 * h);
  if (h <= P2) return 14.1 * Math.exp(-0.0241 * (h - P1));
  if (h <= P3) return 8.1 * Math.exp(-0.01203 * (h - P2));
  return 3.8;
}
function renderEiffel() {
  const p = painter(W, H), cx = EF.x, put = (i, y, col) => p.put(i, y, col);
  const lattice = (dx, y) => (((dx + y) % 6) + 6) % 6 === 0 || (((dx - y) % 6) + 6) % 6 === 0;
  const LEG_TOP = P1 - 5, SPRING = 4;
  const legW = h => lerp(11, 6, clamp(h / LEG_TOP, 0, 1));
  const span = efHW(SPRING) - legW(SPRING);
  for (let y = EF.tip + 12; y <= EF.base; y++) {
    const h = EF.base - y, hw = efHW(h), o = Math.round(hw);
    if (h <= LEG_TOP) {                                         // the two front legs, the arch and its spandrel
      const inner = hw - legW(h);
      for (let dx = -o; dx <= o; dx++) {
        const a = Math.abs(dx), side = dx < 0 ? -1 : 1;
        let col = null;
        if (a > inner) {                                        // leg
          if (a === o) col = side < 0 ? G.hi : G.m;
          else if (a === o - 1) col = side < 0 ? G.l1 : G.l2;
          else if (a <= Math.ceil(inner) + 0.5) col = G.l2;
          else col = lattice(dx, y) ? (side < 0 ? G.l1 : G.l2) : G.s2;
          if (h <= 1) col = h === 0 ? G.s1 : G.m;              // footings
        } else {
          const ha = a < span ? SPRING + (LEG_TOP - SPRING) * Math.sqrt(1 - (a / span) ** 2) : SPRING;
          if (Math.abs(h - ha) < 0.9) col = G.l1;                // the arch's rib
          else if (h > ha) col = lattice(dx, y) && (a + h) % 2 === 0 ? G.l2 : (h - ha < 2 ? G.m : null);   // spandrel
        }
        if (col) put(cx + dx, y, col);
      }
    } else if (h < P1) {                                        // the first platform's girders
      for (let dx = -o - 2; dx <= o + 2; dx++) put(cx + dx, y, h === P1 - 1 ? G.l2 : (dx & 1) ? G.m : G.s1);
    } else if (h <= P1 + 2) {                                   // first platform: rail, then the restaurant's windows
      for (let dx = -o - 2; dx <= o + 2; dx++) {
        let col = h === P1 + 2 ? G.hi : ((dx + 40) % 3 === 0 ? G.m : '#fff6d8');
        if (Math.abs(dx) === o + 2) col = G.l2;
        put(cx + dx, y, col);
      }
    } else if (h < P2) {                                        // between the platforms: the legs still apart
      const gap = lerp(hw * 0.42, 0.5, (h - P1 - 3) / (P2 - P1 - 3));
      for (let dx = -o; dx <= o; dx++) {
        const a = Math.abs(dx);
        if (a < gap) continue;
        let col = a === o ? (dx < 0 ? G.hi : G.m) : a === o - 1 ? (dx < 0 ? G.l1 : G.l2) : a < gap + 1 ? G.l2 : lattice(dx, y) ? G.l1 : G.s2;
        put(cx + dx, y, col);
      }
    } else if (h <= P2 + 2) {                                   // second platform
      for (let dx = -o - 1; dx <= o + 1; dx++) put(cx + dx, y, h === P2 + 2 ? G.hi : h === P2 + 1 ? ((dx & 1) ? G.l1 : '#fff6d8') : G.m);
    } else if (h < P3) {                                        // the upper shaft, tapering
      for (let dx = -o; dx <= o; dx++) {
        const a = Math.abs(dx);
        let col = a === o ? (dx < 0 ? G.hi : G.m) : a === o - 1 && o > 2 ? (dx < 0 ? G.l1 : G.l2) : lattice(dx, y) ? G.l1 : G.s1;
        if ((h - P2) % 16 === 0) col = G.l2;                    // horizontal ties
        put(cx + dx, y, col);
      }
    } else if (h <= P3 + 2) {                                   // third platform: the lit observation deck
      for (let dx = -5; dx <= 5; dx++) put(cx + dx, y, h === P3 + 2 ? G.hi : h === P3 + 1 ? ((dx & 1) ? '#ffffff' : G.l2) : G.m);
    } else if (h <= P3 + 6) {                                   // the campanile on top
      const hw2 = h <= P3 + 4 ? 2 : 1;
      for (let dx = -hw2; dx <= hw2; dx++) put(cx + dx, y, dx === 0 ? '#fff6dc' : dx < 0 ? G.l1 : G.m);
    } else if (h <= P3 + 8) put(cx, y, G.l1);
  }
  for (let y = EF.tip + 1; y < EF.base - P3 - 8; y++) put(cx, y, y < EF.tip + 6 ? '#d8c8a0' : G.m);   // the mast
  return p.done();
}

/* ================= The balloon sign: a blue Montgolfier balloon over a marquee ================= */
function renderBalloon() {
  const p = painter(W, H), put = p.put, cx = BAL.x, T = BAL.top;
  for (let y = T; y <= T + 22; y++) {
    const k = y - T, hw = k <= 10 ? Math.sqrt(Math.max(0, 100 - (10 - k) ** 2)) : 10 - (k - 10) * 0.55;
    const o = Math.round(hw);
    for (let dx = -o; dx <= o; dx++) {
      const u = clamp(dx / Math.max(1, hw), -0.999, 0.999), gore = Math.floor((Math.asin(u) / Math.PI + 0.5) * 8);
      let col = gore & 1 ? '#2a4cbc' : '#3c66dc';
      if (dx > o * 0.4) col = gore & 1 ? '#1e3a96' : '#2c52b8';           // the far side in shade
      if (dx < -o * 0.5 && k > 2) col = gore & 1 ? '#3e64d8' : '#5a84f0';   // lit side
      if (k === 8 || k === 9) col = dx < 0 ? '#ffd65c' : '#d0a038';         // gold band
      if (k >= 3 && k <= 6 && (dx + 40) % 4 === 0 && (k & 1)) col = '#f6d27a';   // little gold stars
      if (k >= 14 && k <= 16) col = k === 15 && (dx & 1) ? '#fff0e0' : '#c8303a';   // red band with its lights
      if (Math.abs(dx) === o) col = (y & 1) ? '#ffe9a8' : '#7a5a2a';         // bulbs down the outline
      put(cx + dx, y, col);
    }
  }
  for (let dx = -3; dx <= 3; dx++) { put(cx + dx, T + 23, '#e8b444'); put(cx + dx, T + 24, '#a8762e'); }
  for (let y = T + 25; y <= T + 28; y++) for (let dx = -3; dx <= 3; dx++) put(cx + dx, y, (dx + y) % 3 === 0 ? '#6a4420' : '#b07c3c');
  for (let y = T + 29; y < 160; y++) for (let dx = -2; dx <= 2; dx++) put(cx + dx, y, dx === -2 ? '#8a7a9a' : '#4a3e5a');
  for (let y = T + 30; y <= T + 34; y++) for (let dx = -6; dx <= 6; dx++) put(cx + dx, y, Math.abs(dx) === 6 || y === T + 30 || y === T + 34 ? '#2a2236' : '#120e1c');
  return p.done();
}
function drawMarquee(t) {                                           // the balloon's LED marquee: slow colour washes
  const T = BAL.top, s = step(t, 120);
  const cols = [['#ff4fa0', '#ffd25a'], ['#4ad8ff', '#ff6ad0'], ['#ffe08a', '#6a8cff']][s % 3];
  for (let y = T + 31; y <= T + 33; y++) for (let dx = -5; dx <= 5; dx++) px(ctx, BAL.x + dx, y, ((dx + y + s) & 3) === 0 ? cols[1] : cols[0]);
}
/* ================= The Bellagio lake ================= */
const WATER_H = FLOOR - LAKE;                                    // the lake also shows between our balusters
const renderWater = () => ditherFill(W, WATER_H, ['#1c1e48', '#16183c', '#111331', '#0d0f27', '#0a0b1e'], (i, y) => y / WATER_H);
// light streaks on the water: [x, width, colour, alpha, seed]
const STREAKS = [
  [EF.x, 7, '#ffd27a', 0.46, 1], [EF.x - 20, 3, '#ffc860', 0.3, 2], [EF.x + 20, 3, '#ffc860', 0.26, 3],
  [BAL.x, 4, '#6a9aff', 0.32, 4],
  [MOON.x, 4, '#f4ecd6', 0.18, 7], [22, 5, '#ffd08a', 0.22, 8], [72, 3, '#ffd08a', 0.18, 9],
];
/* ================= The Bellagio fountains: a calm idle, then the show twice a loop =================
   Between shows the lake breathes: a few low jets bubble along the nozzle line over its underwater lights. Each show
   is 36 s of moves: 'rise' (the row wakes from the middle out), 'wave' (a swell runs down the row), 'chase' (a pulse
   runs back and forth), 'sway' (the arcing oarsmen swing together), 'cross' (they swing against each other), 'shoot'
   (the tall shooters fire one after another), 'finale' (everything at once, the shooters highest), then the mist
   drifts off down the lake. The jets draw into their own layer, which is mirrored into the lake under them. */
const FY = 186, FTOP = 88, FB = FY - FTOP, FCX = 196;          // nozzle line, highest row a jet reaches, the row's middle
const RIP = [], ARCS = [], SHOOT = [70, 110, 148, 178, 300, 326, 350];   // none in front of the diamond sign's words
for (let x = 54; x <= 338; x += 7) RIP.push(x + Math.round((hash(x, 1, 19) - 0.5) * 3));
for (let k = 0; k < 12; k++) ARCS.push(62 + k * 25);
// each nozzle's own strength and brightness: a slow drift along the row plus a little of its own, so no two neighbours match
const RIP_J = RIP.map((x, i) => 0.74 + 0.14 * Math.sin(x * 0.031 + 1.3) + 0.3 * hash(i, 3, 17));
const RIP_B = RIP.map((x, i) => 0.72 + 0.14 * Math.sin(x * 0.047) + 0.22 * hash(i, 5, 17));
const SHOWS = [
  { a: 20, b: 56, secs: [['rise', 0, 5], ['wave', 5, 11], ['cross', 11, 17], ['sway', 17, 24], ['shoot', 24, 31.5], ['finale', 31, 36]] },
  { a: 160, b: 196, secs: [['rise', 0, 5], ['sway', 5, 11], ['chase', 11, 18], ['wave', 18, 24], ['shoot', 24, 31.5], ['finale', 31, 36]] },
];
const SHOT_ORDER = [0, 6, 1, 5, 2, 4, 3];
const SHOT_GAP = 0.95, SHOT_UP = 0.8, SHOT_HOLD = 0.6, SHOT_FALL = 1.5;
const showLevel = t => Math.max(...SHOWS.map(s => env(t, s.a, s.b + 4, 1.5)));
const MISTS = [[24, 98], [164, 236]];                          // spray hanging over the lake, drifting off after each show
const mistLevel = t => Math.max(...MISTS.map(([a, b]) => env(t, a, b, 14)));
let FC, fx, SHOOT_C, JG_C, JET_C, RH;                          // the jet layer and its context, sprites, row heights this frame
/** a water column sprite: bright core, the lower part lit by the lamps under the water, the top breaking up into spray */
function renderColumn(w, h, core, mid, edge, glow, dissolve) {
  const p = painter(w, h), m = (w - 1) / 2;
  for (let y = 0; y < h; y++) {
    const up = 1 - y / (h - 1), top = up > 1 - dissolve ? (up - (1 - dissolve)) / dissolve : 0;
    for (let i = 0; i < w; i++) {
      const d = Math.abs(i - m) / (m + 0.5);
      if (top > 0 && top * (1 + d) > bayer(i, y) + 0.1) continue;
      let col = d < 0.3 ? core : d < 0.65 ? mid : edge;
      if (up < 0.22 && d >= 0.3) col = glow;
      p.put(i, y, col, Math.round((d < 0.3 ? 1 : d < 0.65 ? 0.7 : 0.3) * 255));
    }
  }
  return p.done();
}
function renderJetGlow() {                                     // soft vertical glow, stretched over a jet (drawn additively)
  const w = 15, h = 48, p = painter(w, h);
  for (let y = 0; y < h; y++) for (let i = 0; i < w; i++) {
    const v = Math.pow(Math.max(0, 1 - Math.abs(i - 7) / 7.5), 1.6) * Math.pow(Math.sin(Math.PI * (y + 0.5) / h), 0.5);
    const q = Math.floor(v * 5 + bayer(i, y)) / 5;
    if (q > 0) p.put(i, y, '#cfe2ff', Math.round(q * 0.5 * 255));
  }
  return p.done();
}
/* the row jets, one sprite per height so nothing is stretched: a thin stem of water, bright where the lamps under the
   water light it, thinning as it climbs and opening into a plume of spray that blooms into a soft crown of mist and
   falls back as drops on both sides. Neighbouring crowns merge into one soft crest; JET_V variants make it shimmer. */
const JET_N = 64, JET_V = 4, JET_W = 15, JET_CAP = 9, JET_CH = JET_N + JET_CAP;
function renderJets() {
  const p = painter(JET_W * JET_N, JET_CH * JET_V), m = JET_W >> 1;
  for (let v = 0; v < JET_V; v++) for (let h = 1; h <= JET_N; h++) {
    const ox = (h - 1) * JET_W + m, base = v * JET_CH + JET_CH - 1, top = base - h + 1;
    const on = (i, y, dens) => dens > 0.5 * bayer(ox + i, y) + 0.5 * hash(ox + i, y, 31 + v);
    const spread = 1.6 + h * 0.1, lean = (v - 1.5) * 0.04;          // each variant leans a hair, so neighbours differ
    for (let j = 0; j < h; j++) {
      const u = h > 1 ? j / (h - 1) : 1, y = base - j, brk = smooth(0.35, 1, u), hw = 0.6 + brk * (spread - 0.6), cx = Math.round(lean * j);
      // the column: solid and lamp-lit where it leaves the water, breaking into spray as it climbs
      const core = u < 0.18 ? 1 : 1 - (u - 0.18) * 0.85;
      if (on(cx, y, core)) p.put(ox + cx, y, u < 0.25 ? '#d4e8ff' : '#f2f7ff', Math.round(255 * (0.92 - u * 0.5)));
      for (const sd of [-1, 1]) {
        if (on(cx + sd, y, 0.25 + brk * 0.4)) p.put(ox + cx + sd, y, u < 0.2 ? '#9cc6f4' : '#d8e6fc', Math.round(255 * (0.3 + brk * 0.2)));
        for (let i = 2; i <= Math.ceil(hw); i++) {
          const dens = brk * 0.5 * (1 - (i - 1) / (hw + 0.5));
          if (on(cx + sd * i, y, dens)) p.put(ox + cx + sd * i, y, '#e8f0ff', 120);
        }
        const fall = Math.round(hw + 1 + (1 - u) * h * 0.06);
        if (h > 10 && u > 0.3 && u < 0.92 && on(cx + sd * fall, y, 0.16 * smooth(0.3, 0.55, u))) p.put(ox + cx + sd * fall, y, '#dfecff', 110);
      }
    }
    for (let i = -2; i <= 2; i++) if (i && on(i, base, 0.45 * Math.min(1, h / 16))) p.put(ox + i, base, '#e8f0ff', 140);   // a little froth at its foot
    const cap = Math.min(JET_CAP - 1, 2 + Math.round(h * 0.16)), tx = Math.round(lean * h);
    for (let k = 1; k <= cap; k++) {                                 // the bloom: a soft crown of mist over the top
      const y = top - k, hw = Math.round(spread * (1 - 0.55 * (k / (cap + 1)) ** 2));
      for (let i = -hw; i <= hw; i++) {
        const dens = (1 - k / (cap + 1)) * (1 - Math.abs(i) / (hw + 1.2)) * 1.1;
        if (on(tx + i, y, dens)) p.put(ox + tx + i, y, '#eef4ff', Math.round(255 * clamp(0.25 + 0.5 * dens, 0, 0.8)));
      }
    }
  }
  return p.done();
}
const jetVar = (i, gs) => Math.floor(hash(i, gs, 23) * JET_V);
function jet(x, h, v, a) {                                     // a row jet reaching h px above the water
  h = Math.min(JET_N, Math.round(h));
  if (h < 1 || a <= 0.02) return;
  fx.globalAlpha = Math.min(1, a);
  fx.drawImage(JET_C, (h - 1) * JET_W, v * JET_CH, JET_W, JET_CH, Math.round(x) - (JET_W >> 1), FB - JET_CH, JET_W, JET_CH);
  fx.globalAlpha = 1;
}
function jetColumn(x, h0, h1, spr, a) {                        // a column from h0 to h1 px above the water
  if (h1 - h0 < 1 || a <= 0.02) return;
  fx.globalAlpha = Math.min(1, a);
  fx.drawImage(spr, Math.round(x - (spr.width >> 1)), Math.round(FB - h1), spr.width, Math.round(h1 - h0));
  fx.globalAlpha = 1;
}
function sprinkle(x, y, r, n, seed, a, gs, col, wide = 1.4, tall = 0.8) {   // shimmering droplets round a point
  fx.fillStyle = col;
  for (let k = 0; k < n; k++) {
    const ang = hash(seed, k, gs) * TAU, d = Math.sqrt(hash(seed + 7, k, gs));
    fx.globalAlpha = a * (1 - d * 0.7);
    fx.fillRect(Math.round(x + Math.cos(ang) * d * r * wide), Math.round(y + Math.sin(ang) * d * r * tall), 1, 1);
  }
  fx.globalAlpha = 1;
}
function plume(x, top, len, spread, n, seed, a, gs) {          // a feathery crown of spray over a shooter, widest at the top
  fx.fillStyle = '#f4f8ff';
  for (let k = 0; k < n; k++) {
    const f = Math.pow(hash(seed, k, gs), 0.8), w = spread * (1 - f * 0.75);
    fx.globalAlpha = a * (0.45 + 0.55 * (1 - f));
    fx.fillRect(Math.round(x + (hash(seed + 5, k, gs) * 2 - 1) * w), Math.round(top + f * len), 1, 1);
  }
  fx.globalAlpha = 1;
}
function cascade(x, top, spread, n, seed, a, gs) {             // droplets raining back down round a column
  fx.fillStyle = '#dfecff';
  for (let k = 0; k < n; k++) {
    const f = hash(seed, k, gs), side = hash(seed + 1, k, gs) < 0.5 ? -1 : 1;
    fx.globalAlpha = a * (1 - f * 0.6);
    fx.fillRect(Math.round(x + side * spread * (0.45 + 0.55 * Math.sqrt(f))), Math.round(top + f * (FB - top)), 1, 1);
  }
  fx.globalAlpha = 1;
}
function jetArc(x, dx, h, a, seed, gs, tops) {                 // an oarsman: a parabola from the nozzle, apex h up and dx across
  if ((x + dx > 176 && x + dx < 276) || (x > 176 && x < 276)) h = Math.min(h, 25);   // low in front of the diamond sign, so its words read
  if (h < 2 || a <= 0.02) return;
  const n = Math.round((h * 2 + Math.abs(dx)) * 1.2);
  for (let k = 0; k <= n; k++) {
    const s = k / n * 1.95, X = Math.round(x + dx * s), Y = Math.round(FB - h * (2 * s - s * s));
    if (s > 1.05 && hash(seed, k, gs) < (s - 1.05) * 0.8) continue;              // the falling half breaks up
    const fa = a * (s < 1 ? 1 : 1.3 - s * 0.4);
    fx.globalAlpha = fa; fx.fillStyle = '#ffffff'; fx.fillRect(X, Y, 1, 1);
    fx.globalAlpha = fa * 0.5; fx.fillStyle = s < 0.45 ? '#a9d0ff' : '#e2eeff'; fx.fillRect(X, Y + 1, 1, 1);
  }
  fx.globalAlpha = 1;
  sprinkle(x + dx * 1.95, FB - 1, 2, 4, seed + 9, a * 0.7, gs, '#eef4ff');       // splash where it lands
  sprinkle(x + dx, FB - h, 2, 3, seed + 3, a * 0.6, gs, '#eef4ff');
  if (tops && h > 10) tops.push([x + dx, FY - h, a * 0.35, h * 0.85]);
}
/** one shooter burst s seconds after firing: a wide column racing up, a plume, then collapsing into falling spray */
function shot(x, s, hmax, seed, gs, tops) {
  if (s < 0 || s > SHOT_UP + SHOT_HOLD + SHOT_FALL) return;
  let h0 = 0, h1, a = 1, fall = 0;
  if (s < SHOT_UP) h1 = hmax * (1 - (1 - s / SHOT_UP) ** 2);
  else if (s < SHOT_UP + SHOT_HOLD) h1 = hmax + (s - SHOT_UP) * 4;
  else { fall = (s - SHOT_UP - SHOT_HOLD) / SHOT_FALL; h1 = (hmax + SHOT_HOLD * 4) * (1 - fall * fall * 0.85); h0 = hmax * 0.85 * fall; a = 1 - fall * 0.7; }
  const top = FB - h1, grow = Math.min(1, s / SHOT_UP);
  jetColumn(x, h0, h1, SHOOT_C, a);
  plume(x, top - 2, 6 + grow * 10, 2 + grow * 4 + fall * 6, 34, seed, 0.95 * a, gs);
  if (s > SHOT_UP * 0.7) cascade(x, top + 4, 4 + grow * 3 + fall * 8, 26, seed + 40, 0.7 * a * Math.min(1, (s - SHOT_UP * 0.7) / 0.5), gs);
  tops.push([x, FY - h1, a, h1 - h0]);
}
const pingpong = v => { const f = frac(v / 2) * 2; return f < 1 ? f : 2 - f; };
const archAt = x => 0.5 + 0.5 * Math.cos(Math.PI * clamp((x - FCX) / 160, -1, 1));   // the row stands taller towards its middle
function drawFountain(t) {
  const gs = step(t, 2400), tops = [];
  fx.setTransform(1, 0, 0, 1, 0, 0); fx.globalAlpha = 1; fx.globalCompositeOperation = 'source-over'; fx.clearRect(0, 0, W, FB + 1);
  RH.fill(0);
  const ripple = (i, h, a) => { h *= RIP_J[i]; RH[i] = Math.max(RH[i], h * a); jet(RIP[i], h, jetVar(i, gs), a * RIP_B[i]); };
  const idle = 1 - showLevel(t);
  if (idle > 0.02) RIP.forEach((x, i) => {                      // the calm idle: low jets bubbling, breathing slowly
    if (i % 3) return;
    const b = 0.5 + 0.5 * Math.sin(TAU * 8 * t / LOOP + i * 0.9);
    ripple(i, (4 + 6 * b) * (0.7 + 0.5 * archAt(x)), 0.8 * idle);
  });
  const S = SHOWS.find(s => t >= s.a - 0.1 && t < s.b + 3);
  if (!S) return tops;
  const u = t - S.a;
  for (const [kind, s0, s1] of S.secs) {
    if (u < s0 - 0.1 || u > s1 + 3.5) continue;
    const e = env(u, s0, s1, 0.7), v = u - s0;
    if (kind === 'rise') {                                      // the oarsmen wake from the middle out, each arc climbing
      ARCS.forEach((x, k) => {
        const dl = Math.abs(x - FCX) / 150 * 2.2, g = smooth(dl, dl + 1.4, v);
        jetArc(x, (k % 2 ? 1 : -1) * (4 + 6 * g), (10 + 26 * archAt(x)) * g * e, 0.9, 40 + k, gs, tops);
      });
    } else if (kind === 'wave') {                               // a swell runs down the line of arcs, which lean with it
      ARCS.forEach((x, k) => {
        const sw = 0.5 + 0.5 * Math.sin(x * 0.042 - v * 3.4);
        jetArc(x, 10 * Math.sin(v * 1.6 - k * 0.5), (12 + 32 * sw) * e, 0.9, 50 + k, gs, tops);
      });
    } else if (kind === 'chase') {                              // a pulse of tall jets runs back and forth along the row
      const xp = [0, 0.35, 0.7].map(d => 50 + 292 * pingpong((v - d) / 3.4)), bump = d => smooth(0, 1, 1 - Math.abs(d) / 24);
      RIP.forEach((x, i) => {
        const b = Math.max(bump(x - xp[0]), 0.72 * bump(x - xp[1]), 0.45 * bump(x - xp[2]));
        if (b > 0.08) ripple(i, (8 + 44 * b) * e, 0.95);
      });
      ARCS.forEach((x, k) => { if (k % 3 === 1) jetArc(x, (k % 2 ? 6 : -6), 14 * e, 0.7, 70 + k, gs); });
    } else if (kind === 'sway' || kind === 'cross') {
      const sw = Math.sin(v * 1.8);
      ARCS.forEach((x, k) => {
        const dx = kind === 'sway' ? 22 * sw + (k - 5.5) * 1.2 : (k % 2 ? 1 : -1) * 20 * sw;
        jetArc(x, dx, (38 + 4 * Math.sin(k * 1.7)) * e, 0.95, 60 + k, gs, tops);
      });
    } else if (kind === 'shoot') {
      SHOT_ORDER.forEach((k, i) => shot(SHOOT[k], v - i * SHOT_GAP, k === 3 ? 84 : 58 + (k % 3) * 7, 80 + k, gs, tops));
      ARCS.forEach((x, k) => { if (k % 2) jetArc(x, (k % 4 === 1 ? 8 : -8), 12 * e, 0.7, 100 + k, gs); });
    } else if (kind === 'finale') {
      SHOOT.forEach((x, k) => shot(x, v - 0.2 - Math.abs(k - 3) * 0.1, k === 3 ? 92 : 74 - Math.abs(k - 3) * 3, 90 + k, gs, tops));
      const f = e * smooth(0, 0.6, v);
      ARCS.forEach((x, k) => jetArc(x, (k - 5.5) * 5, 46 * f, 0.9, 120 + k, gs, tops));
    }
  }
  return tops;
}
/** composite the jet layer: a veil of lit spray, the jets, their mirror in the lake, glows on the tall ones, the lights under the water */
function drawFountainLayer(t, tops) {
  const lv = showLevel(t);
  for (let x = RIP[0]; x <= RIP[RIP.length - 1]; x += 24) halo(HL.nozzle, x, FY + 1, 0.35 + lv * (0.35 + 0.15 * wave(t, 300, x / 97)));
  halo(HL.fountain, FCX, FY - 18, lv * 0.9);
  ctx.globalCompositeOperation = 'lighter';
  for (let g = 0; g < RIP.length; g += 6) {
    let hs = 0;
    for (let i = g; i < g + 6; i++) hs += RH[i] || 0;
    const h = hs / 6;
    if (h < 4) continue;
    ctx.globalAlpha = 0.4 * Math.min(1, h / 16);
    ctx.drawImage(JG_C, RIP[g] - 6, Math.round(FY - h - 5), 32, Math.round(h) + 7);
  }
  ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  ctx.drawImage(FC, 0, FTOP);
  const rows = FLOOR - FY - 2;
  for (let k = 0; k < rows; k++) {                              // the jets mirrored in the lake, rippling
    const sy = FB - 1 - Math.floor(k * 1.7);
    if (sy < 0) break;
    const off = Math.round(Math.sin(TAU * 60 * t / LOOP + k * 0.8) * (0.6 + k * 0.08));
    ctx.globalAlpha = 0.26 * (1 - k / rows);
    ctx.drawImage(FC, 0, sy, W, 1, off, FY + 1 + k, W, 1);
  }
  ctx.globalAlpha = 1;
  ctx.globalCompositeOperation = 'lighter';
  for (const [x, y, a, h] of tops) if (h > 0) { ctx.globalAlpha = 0.5 * a; ctx.drawImage(JG_C, x - 7, Math.round(y) - 6, 15, Math.round(h) + 8); }
  ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  for (const [x, y, a] of tops) halo(HL.jetTop, x, y, a * 0.9);
  if (lv <= 0) return;
  const gs = step(t, 2400);
  ctx.fillStyle = '#eef4ff';
  for (let k = 0; k < 60; k++) {                                // spray landing along the nozzle line
    if (hash(k, gs, 71) > lv * 0.8) continue;
    ctx.globalAlpha = 0.6; ctx.fillRect(RIP[0] + Math.floor(hash(k, gs, 72) * 290), FY - 1 + Math.floor(hash(k, gs, 73) * 5), 1, 1);
  }
  ctx.globalAlpha = 1;
}
/* the mist: a pale veil that grows over the jets and drifts off down the lake */
function makeMist() {
  const h = 70, [c, cx] = makeCanvas(W, h), img = cx.createImageData(W, h), d = img.data, A = hex('#c8d0e8'), B = hex('#e8eefc');
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    const v = y / (h - 1), prof = Math.pow(Math.sin(Math.PI * Math.pow(v, 1.3)), 1.4);
    let nz = 0.45 + 0.3 * Math.sin(TAU * 3 * x / W + y * 0.09) + 0.22 * Math.sin(TAU * 7 * x / W - y * 0.23 + 1.3) + 0.12 * Math.sin(TAU * 17 * x / W + y * 0.5);
    nz = Math.max(0, nz) ** 1.6 * 1.3;
    const q = Math.floor(clamp(prof * nz, 0, 1) * 5 + bayer(x, y)) / 5;
    if (q <= 0) continue;
    const o = (y * W + x) * 4;
    d[o] = A[0] + (B[0] - A[0]) * v; d[o + 1] = A[1] + (B[1] - A[1]) * v; d[o + 2] = A[2] + (B[2] - A[2]) * v; d[o + 3] = Math.round(q * 255);
  }
  cx.putImageData(img, 0, 0);
  return c;
}
function drawMist(t) {
  const m = mistLevel(t);
  if (m <= 0.01) return;
  ctx.globalAlpha = m * 0.3;
  drawScroll(MIST_C, FY - 62, 3, t);
  ctx.globalAlpha = 1;
}
function drawLake(t, billboard, pics, hrCol) {
  ctx.drawImage(WATER_C, 0, LAKE);
  reflect(ctx, t, { top: LAKE, rows: WATER_H, squash: 1.3, alpha: 0.5, amp: 0.45, grow: 0.04 });
  ctx.globalAlpha = 0.16; rect(ctx, 0, LAKE, W, WATER_H, '#06061a'); ctx.globalAlpha = 1;
  rect(ctx, 0, LAKE, W, 1, '#2a2240');
  for (const [x, w, col, a, seed] of STREAKS) streak(ctx, t, x, w, col, a, seed, LAKE + 1, FLOOR);
  if (billboard) streak(ctx, t, DIA.x, 5, '#ffd88a', 0.26, 10, LAKE + 1, FLOOR);
  for (const [k, , a] of pics) streak(ctx, t, SPH.x, 5, SPH_GLOW[k], 0.3 * a, 5, LAKE + 1, FLOOR);
  streak(ctx, t, HRW.x, 4, hrCol, 0.26, 6, LAKE + 1, FLOOR);
  for (const lx of FAR_LAMPS) streak(ctx, t, lx, 1, '#ffd890', 0.2, lx, LAKE + 1, LAKE + 16);
}

/* ================= Our balustrade on the lake, and its lamp posts ================= */
const BALUSTER = [[199, 1], [200, 0], [201, 1], [202, 1], [203, 0], [204, 1]];   // [row, half-width] of a turned baluster
const PEDESTALS = [...LAMPS, 38, 94, 194, 296, 352];
function renderBalustrade() {
  const p = painter(W, H);
  const glow = i => Math.max(0, ...LAMPS.map(lx => 1 - Math.abs(i - lx) / 34), 1 - Math.abs(i - NEAR_LAMP.x) / 40);
  const put = (i, y, col) => p.put(i, y, tint(col, '#ffcf8a', Math.round(glow(i) * 4 + bayer(i, y) - 0.5) / 4 * 0.45));
  for (let i = 0; i < W; i++) {
    put(i, RAIL, '#cbbba0'); put(i, RAIL + 1, '#ab9a80'); put(i, RAIL + 2, '#76664f');
    put(i, 205, '#a8977c'); put(i, 206, '#86765e'); put(i, 207, '#4e4038');
  }
  for (let bx = 2; bx < W; bx += 4) for (const [y, hw] of BALUSTER) for (let k = -hw; k <= hw; k++)
    put(bx + k, y, k < 0 ? '#c4b296' : k > 0 ? '#5e4e40' : '#94826a');
  for (const x of PEDESTALS) {
    for (let y = RAIL - 3; y <= 207; y++) for (let k = -3; k <= 3; k++) {
      let col = k === -3 ? '#d2c2a4' : k === 3 ? '#5e4e40' : '#a8977a';
      if (y <= RAIL - 2) col = y === RAIL - 3 ? '#e2d4b8' : '#92806a';
      else if (y === RAIL - 1 || y === 205) col = '#bcab8e';
      put(x + k, y, col);
    }
    put(x - 4, RAIL - 3, '#bcab8e'); put(x + 4, RAIL - 3, '#6e5c48'); put(x - 4, RAIL - 2, '#86745e'); put(x + 4, RAIL - 2, '#4e4038');
  }
  for (const x of LAMPS) {                                        // black iron posts with a three-globe head
    for (let y = 170; y < RAIL - 3; y++) { p.put(x, y, '#2e2636'); p.put(x + 1, y, '#120e18'); }
    for (const [dx, dy] of [[-1, 190], [2, 190], [-1, 189], [2, 189]]) p.put(x + dx, dy, '#120e18');
    for (let k = -4; k <= 5; k++) p.put(x + k, 171, '#120e18');
    for (const gx of [x - 4, x + 5]) p.put(gx, 170, '#120e18');
  }
  return p.done();
}
const GLOBES = [];
for (const x of LAMPS) GLOBES.push([x, 166], [x - 4, 168], [x + 5, 168]);
function drawLampHeads() {
  for (const [gx, gy] of GLOBES) {
    rect(ctx, gx - 1, gy - 1, 3, 3, '#fff4d2'); px(ctx, gx, gy - 2, '#ffe6a6'); px(ctx, gx - 1, gy + 1, '#f2c27a'); px(ctx, gx + 1, gy + 1, '#d89a58');
    halo(HL.globe, gx, gy, 0.85);
  }
  for (const x of LAMPS) halo(HL.lampWide, x, 168, 0.9);
}

/* ================= People (after rome.js's person()) ================= */
// A look gives the proportions for a height and the colours dimmed for the night, with the lamps' light on the
// shoulders and hair; person() draws it from behind (watching the fountains), facing us or in profile, arms doing
// something. Everyone is drawn in the order of their feet (DL), so nearer people cover further ones.
const R = (x, y, w, h, c) => { ctx.fillStyle = c; ctx.fillRect(x, y, w, h); };
const SKINS = ['#f2c8a0', '#e2ac82', '#c48c60', '#8c5a3c'], HAIRS = ['#1c1412', '#3c2418', '#6c4222', '#d0a858', '#96421c', '#d4d0cc'];
const NIGHT = '#16102a', GLOWC = '#ffe2b8';
function look(h, skin, hair, top, bot, o = {}) {
  const dk = o.dk ?? 0.18, m = c => tint(c, NIGHT, dk);
  const hh = h >= 22 ? 5 : h >= 14 ? 4 : 3, th = Math.round(h * 0.36);
  return { h, hh, hw: h >= 19 ? 4 : 3, th, tw: h >= 22 ? 7 : h >= 19 ? 6 : h >= 14 ? 5 : 4, ll: h - hh - th,
    skin: m(SKINS[skin]), hair: m(HAIRS[hair]), hairL: tint(HAIRS[hair], GLOWC, 0.3), top: m(top), topD: tint(top, NIGHT, dk + 0.3),
    topL: tint(top, GLOWC, 0.35), bot: m(bot), shoe: m(o.shoe || '#221a20'), long: o.long || 0, skirt: o.skirt ? m(o.skirt) : null,
    hat: o.hat ? m(o.hat) : null, brim: !!o.brim, cap: !!o.cap, gown: !!o.gown, plume: !!o.plume, bow: !!o.bow };
}
/**
 * One person, feet on row fy, centred on column x; returns the row of the top of the head.
 * o.view: 0 from behind, 1 facing us, 2 in profile (o.dir 1 right, -1 left); o.step: stride frame; o.sit: seated at fy;
 * o.lean: head px towards a neighbour; o.aL / o.aR: 0 down, 1 straight up, 2 bent up with a phone, 3 out, 4 bent up
 * (clapping, holding on), 7 hand on the hip.
 */
function person(x, fy, P, o = {}) {
  x = Math.round(x); fy = Math.round(fy);
  const { hw, hh, tw, th } = P, view = o.view || 0, dir = o.dir || 1, side = view === 2;
  const legs = o.sit ? 0 : P.ll;
  const ty = fy - legs - th + 1, hy = ty - hh, bw = side ? tw - 2 : tw, tx = x - (bw >> 1), legC = P.skirt ? P.skin : P.bot;
  if (legs) {
    const ly = fy - legs + 1;
    if (P.gown) { R(tx, ly, bw, legs, P.bot); R(tx - 1, ly + 2, bw + 2, legs - 2, P.bot); R(tx - 2, fy - 1, bw + 4, 2, P.bot); R(tx + bw, ly + 2, 1, legs - 2, P.topD); }
    else if (side) {
      if (o.step) {
        R(x - 2, ly, 4, 2, legC);
        R(dir > 0 ? x + 1 : x - 2, ly + 2, 2, legs - 2, legC); R(dir > 0 ? x + 1 : x - 3, fy, 3, 1, P.shoe);
        R(dir > 0 ? x - 2 : x + 1, ly + 2, 2, legs - 3, legC); R(dir > 0 ? x - 2 : x + 1, fy - 1, 2, 1, P.shoe);
      } else { R(x - 1, ly, 2, legs, legC); R(dir > 0 ? x - 1 : x - 2, fy, 3, 1, P.shoe); }
      if (P.skirt) R(x - 2, ly, 4, (legs >> 1) + 1, P.skirt);
    } else {
      const lw = bw >= 7 ? 3 : 2;
      R(tx, ly, bw, 2, P.bot); R(tx, ly + 2, lw, legs - 2, legC); R(tx + bw - lw, ly + 2, lw, legs - 2, legC);
      R(tx, fy, lw, 1, P.shoe); R(tx + bw - lw, fy, lw, 1, P.shoe);
      if (P.skirt) R(tx, ly, bw, (legs >> 1) + 1, P.skirt);
    }
  } else if (o.sit) R(tx - 1, fy - 1, bw + 2, 2, P.bot);
  R(tx, ty, bw, th, P.top);
  if (!side) R(tx + bw - 1, ty + 1, 1, th - 1, P.topD);
  if (view !== 1) R(tx, ty, bw, 1, P.topL);                                    // the lamps' light on the shoulders
  if (P.bow && view === 1) { R(x - 1, ty, 3, 1, '#f2eee8'); R(x, ty + 1, 1, th - 2, '#f2eee8'); }   // white shirt and tie
  const arm = (ax, st, sg) => {
    if (!st) { R(ax, ty + 1, 1, th - 2, P.topD); R(ax, ty + th - 1, 1, 1, P.skin); }
    else if (st === 1) { R(ax, hy - 1, 1, ty - hy + 2, P.topD); R(ax, hy - 3, 1, 2, P.skin); }
    else if (st === 2 || st === 4) {
      R(ax, hy + 2, 1, ty - hy, P.topD); R(ax, hy + 1, 1, 1, P.skin);
      if (st === 2) { const p0 = sg < 0 ? ax - 1 : ax; R(p0, hy - 2, 2, 3, '#0e1018'); if (view === 0) { R(p0, hy - 2, 2, 1, '#e8f2ff'); R(p0, hy - 1, 2, 1, '#8ac8ff'); } }
    } else if (st === 3) { R(sg < 0 ? ax - 2 : ax, ty + 1, 3, 1, P.topD); R(sg < 0 ? ax - 3 : ax + 3, ty + 1, 1, 1, P.skin); }
    else if (st === 7) { R(ax, ty + 1, 1, 2, P.topD); R(ax + sg, ty + 3, 1, 1, P.skin); }
  };
  if (side) {
    const st = o.aR || 0;
    if (st === 1) { R(x + dir, hy - 1, 1, ty - hy + 2, P.topD); R(x + dir, hy - 3, 1, 2, P.skin); }
    else if (st === 3) { R(dir > 0 ? x + 1 : x - 3, ty + 2, 3, 1, P.topD); R(x + dir * 4, ty + 2, 1, 1, P.skin); }
    else { const ax = x + (o.step ? -dir : 0); R(ax, ty + 1, 1, th - 2, P.topD); R(ax, ty + th - 1, 1, 1, P.skin); }
  } else { arm(tx - 1, o.aL, -1); arm(tx + bw, o.aR, 1); }
  const hx = x - (hw >> 1) + (o.lean || 0), hr = hh >= 5 ? 2 : 1;
  if (view === 0) {
    R(hx, hy, hw, hh, P.hair); R(hx, hy, hw, 1, P.hairL);
    if (P.long) R(hx, hy + hh, hw, P.long, P.hair); else R(hx + 1, hy + hh - 1, hw - 2, 1, P.skin);
  } else if (view === 1) {
    R(hx, hy, hw, hh, P.skin); R(hx, hy, hw, hr, P.hair);
    if (P.long) { R(hx - 1, hy + 1, 1, hh + P.long - 1, P.hair); R(hx + hw, hy + 1, 1, hh + P.long - 1, P.hair); }
  } else { R(hx, hy, hw, hh, P.skin); R(hx, hy, hw, hr, P.hair); R(dir > 0 ? hx : hx + hw - 1, hy, 1, hh - 1 + P.long, P.hair); }
  if (P.hat) {                                                                  // a hat; a cowboy hat has a wide brim
    R(hx, hy - 1, hw, 2, P.hat);
    if (P.brim) R(hx - 2, hy, hw + 4, 1, P.hat); else if (side) R(dir > 0 ? hx + hw : hx - 1, hy, 1, 1, P.hat); else R(hx - 1, hy, hw + 2, 1, P.hat);
  }
  if (P.cap) { R(hx, hy - 1, hw, 2, '#14121c'); R(side ? (dir > 0 ? hx + hw - 1 : hx - 1) : hx - 1, hy + 1, side ? 2 : hw + 2, 1, '#0c0a12'); }
  if (P.gown) { R(hx - 1, hy, 1, hh + 6, '#f6f2ec'); R(hx + hw, hy, 1, hh + 6, '#f6f2ec'); R(hx, hy - 1, hw, 1, '#f6f2ec'); }   // her veil
  if (P.plume) {                                                                // a showgirl's feather fan
    const fx0 = hx + (hw >> 1);
    for (let f = -3; f <= 3; f++) for (let k = 1; k <= 8 - Math.abs(f); k++) {
      const X = fx0 + Math.round(f * k * 0.42), Y = hy - k;
      R(X, Y, 1, 1, k > 6 - Math.abs(f) ? '#ff9ad0' : (f & 1) ? '#fff6fa' : '#f0d8e8');
    }
    R(hx, hy - 1, hw, 1, '#ffd65a');
  }
  return hy;
}
const DL = [];                                                   // the frame's draw list: [feet row, draw]
const put = (fy, draw) => DL.push([fy, draw]);
const flushDL = () => { DL.sort((a, b) => a[0] - b[0]); for (const [, d] of DL) d(); DL.length = 0; };
const STRIDE = 768;                                              // a walker's step changes 3.2 times a second

/* ----- the people at the rail, watching the fountains ----- */
const CROWD = 0.55;
const RAILERS = [
  { x: 50, P: look(16, 1, 1, '#c84a5a', '#2c3458', { long: 2 }), verb: 'film', ph: 0.1, rank: 0.08 },
  { x: 104, P: look(17, 2, 0, '#2e3a6a', '#4a4a5a'), verb: 'point', ph: 0.4, rank: 0.31 },
  { x: 110, P: look(16, 0, 3, '#ece2f0', '#ece2f0', { long: 3, skirt: '#ece2f0', shoe: '#8a2a3a' }), verb: 'lean', ph: 0.4, rank: 0.31 },
  { x: 160, P: look(16, 1, 4, '#3aa0a0', '#30304a', { long: 2 }), verb: 'film', ph: 0.6, rank: 0.74 },
  { x: 196, P: look(17, 1, 1, '#3a7a62', '#2a3040'), verb: 'carry', ph: 0.2, rank: 0.22 },
  { x: 244, P: look(17, 2, 0, '#8a3a2a', '#3a3440', { hat: '#8a6440', brim: true }), verb: 'look', ph: 0.7, rank: 0.86 },
  { x: 288, P: look(16, 3, 0, '#d8b050', '#d8b050', { long: 3, skirt: '#d8b050' }), verb: 'film', ph: 0.3, rank: 0.45 },
  { x: 295, P: look(16, 0, 4, '#2aa8a0', '#2aa8a0', { long: 2, skirt: '#2aa8a0' }), verb: 'chat', ph: 0.5, rank: 0.45 },
  { x: 318, P: look(17, 1, 5, '#7a5a3a', '#3a3440'), verb: 'clap', ph: 0.9, rank: 0.66 },
];
const CHILD = look(11, 0, 3, '#ffcf4a', '#3a5aa8', { dk: 0.1 });
const finaleOn = t => SHOWS.some(s => t >= s.a + 24 && t < s.b + 1);
function drawRailers(t, crowd) {
  const lv = showLevel(t), fin = finaleOn(t);
  for (const r of RAILERS) {
    if (r.rank >= crowd) continue;
    const o = { view: 0 }, cyc = frac(t / 24 + r.ph), c40 = frac(t / 40 + r.ph);
    if (r.verb === 'film') o.aR = lv > 0.3 || cyc < 0.28 ? 2 : 0;
    else if (r.verb === 'point') o.aR = (fin && frac(t / 6 + r.ph) < 0.4) || c40 < 0.12 ? 3 : 0;
    else if (r.verb === 'lean') o.lean = c40 < 0.6 ? -1 : 0;
    else if (r.verb === 'chat') { if (frac(t / 30 + r.ph) < 0.22) { o.view = 2; o.dir = -1; } }
    else if (r.verb === 'clap') { if (fin && (step(t, 1200) & 1)) { o.aL = 4; o.aR = 4; } }
    else if (r.verb === 'look') o.lean = frac(t / 20 + r.ph) < 0.3 ? 1 : 0;
    if (r.verb === 'carry') {                                    // a child on his shoulders, arms up at the finales
      const up = fin || frac(t / 12) < 0.2;
      person(r.x, 207 - r.P.ll - r.P.th - 1, CHILD, { sit: true, aL: up ? 1 : 0, aR: up && (step(t, 480) & 1) ? 1 : (up ? 4 : 0) });
      o.aL = 4; o.aR = 4;
    }
    const hy = person(r.x, 207, r.P, o);
    if (o.aR === 2 && o.view === 0) halo(HL.phone, r.x + 3, hy - 1, 0.55);
  }
}

/* ----- strollers on the walk: two lanes, each crossing a whole number of times a loop ----- */
const LANES = [222, 242];
const WALKERS = [
  { lane: 0, dir: 1, k: 4, ph: 0.05, P: look(19, 0, 2, '#4a6ac8', '#33466e'), rank: 0.15 },
  { lane: 0, dir: -1, k: 3, ph: 0.42, P: look(18, 2, 0, '#e87aa8', '#22202c', { long: 3 }), P2: look(19, 1, 3, '#ece6da', '#3a3a58', { long: 2 }), rank: 0.5 },
  { lane: 1, dir: 1, k: 3, ph: 0.68, P: look(21, 1, 0, '#8a4a8a', '#22202c', { long: 3 }), dog: true, rank: 0.27 },
  { lane: 1, dir: -1, k: 4, ph: 0.25, P: look(21, 2, 1, '#e8823a', '#22202c'), bag: true, rank: 0.62 },
  { lane: 0, dir: -1, k: 5, ph: 0.86, P: look(18, 3, 0, '#c8d0e0', '#3a3a58'), rank: 0.92 },
];
const DOGR = ['....ee', 'tdddd.', '.dddd.', '.d..d.'], DOGR2 = ['....ee', 'tdddd.', '.dddd.', 'd....d'];
function drawWalkers(t, crowd) {
  const fr = step(t, STRIDE) & 1;
  for (const w of WALKERS) {
    if (w.rank >= crowd) continue;
    const u = frac(w.k * t / LOOP + w.ph), x = w.dir > 0 ? lerp(-24, 504, u) : lerp(504, -24, u), fy = LANES[w.lane];
    if (x < -20 || x > 500) continue;
    put(fy, () => {
      ctx.globalAlpha = 0.4; R(Math.round(x) - 3, fy + 1, 7, 1, '#0a0610'); ctx.globalAlpha = 1;
      person(x, fy, w.P, { view: 2, dir: w.dir, step: fr });
      if (w.P2) person(x - w.dir * 7, fy + 1, w.P2, { view: 2, dir: w.dir, step: fr ^ 1 });
      if (w.bag) { R(Math.round(x) - w.dir * 3 - 1, fy - 10, 3, 4, '#d8b070'); R(Math.round(x) - w.dir * 3, fy - 11, 1, 1, '#8a6a40'); }
      if (w.dog) {
        const dx = Math.round(x + w.dir * 14), rows = fr ? DOGR : DOGR2;
        rows.forEach((row, j) => { for (let i = 0; i < 6; i++) if (row[i] !== '.') R(w.dir > 0 ? dx + i : dx + 5 - i, fy - 3 + j, 1, 1, '#c8a070'); });
        line(ctx, Math.round(x) + w.dir * 2, fy - 13, dx + (w.dir > 0 ? 4 : 1), fy - 3, '#c84a5a');
      }
    });
  }
}

/* ----- the bench couple, her head on his shoulder ----- */
const BENCH = { x: 100, y: 236 };
const SITTERS = [{ dx: 9, P: look(20, 1, 0, '#3a4a7a', '#2a2a3a'), rank: 0.38 }, { dx: 16, P: look(19, 0, 4, '#b84a5a', '#2a2a3a', { long: 2 }), rank: 0.38 }];
function drawBench(t, crowd) {
  put(BENCH.y, () => {
    for (const [k, s] of SITTERS.entries()) if (s.rank < crowd) person(BENCH.x + s.dx, BENCH.y - 8, s.P, { sit: true, view: 0, lean: k === 1 && frac(t / 40) < 0.7 ? -1 : 0 });
    ctx.drawImage(BENCH_C, BENCH.x, BENCH.y - 21);
  });
}

/* ----- the showgirl by the lamp, and the tourists who stop for a photo with her ----- */
const SHOWGIRL = { x: 58, fy: 232, P: look(22, 0, 3, '#e8c040', '#e8c040', { plume: true, skirt: '#e8c040', long: 2, shoe: '#e8e8f0', dk: 0.08 }) };
const PHOTOS = [
  { a: 62, b: 88, A: look(21, 2, 0, '#2a9a8a', '#d8d4cc'), B: look(22, 0, 2, '#e8c040', '#33466e', { long: 3 }) },
  { a: 204, b: 230, A: look(21, 1, 1, '#d8404a', '#33466e', { hat: '#d8d4cc' }), B: look(22, 3, 0, '#58b0d8', '#22202c') },
];
function drawShowgirl(t, crowd) {
  if (crowd <= 0) return;
  const ph = PHOTOS.find(p => t >= p.a && t < p.b), posing = ph && t >= ph.a + 6 && t < ph.a + 20;
  put(SHOWGIRL.fy, () => {
    const wave = frac(t / 8) < 0.2;
    const hy = person(SHOWGIRL.x, SHOWGIRL.fy, SHOWGIRL.P, { view: 1, aL: posing ? 3 : 7, aR: posing ? 3 : wave ? 1 : 7 });
    const s = step(t, 960);                                       // sequins catching the lamp
    for (let k = 0; k < 3; k++) R(SHOWGIRL.x - 3 + Math.floor(hash(k, s, 91) * 7), hy + 5 + Math.floor(hash(k, s, 92) * 9), 1, 1, '#fffbe8');
  });
  if (!ph) return;
  const u = t - ph.a, fr = step(t, STRIDE) & 1;
  const walk = (x0, x1, fy, P, still) => {
    let x, o;
    if (u < 6) { x = lerp(-14, x1, u / 6); o = { view: 2, dir: 1, step: fr }; }
    else if (u < 20) { x = x1; o = still; }
    else { x = lerp(x1, -14, (u - 20) / 6); o = { view: 2, dir: -1, step: fr }; }
    put(fy, () => { ctx.globalAlpha = 0.4; R(Math.round(x) - 3, fy + 1, 7, 1, '#0a0610'); ctx.globalAlpha = 1; const hy = person(x, fy, P, o); if (o.aR === 2) halo(HL.phone, x + 3, hy - 1, 0.6); });
  };
  walk(-14, SHOWGIRL.x + 9, SHOWGIRL.fy + 1, ph.A, { view: 1, aL: 3, aR: 1 });
  walk(-14, SHOWGIRL.x + 4, SHOWGIRL.fy + 16, ph.B, { view: 0, aR: 2 });
}

/* ----- the wedding: the doors open, the guests throw confetti, the couple walk the carpet to the car and drive off;
   later the chapel's driver brings the car back and goes in ----- */
const WED = { open: 84, close: 142, out: 90, kiss: 114, seated: 118, drive: [124, 142], back: [200, 216], driver: [218, 236] };
const BRIDE = look(21, 0, 3, '#f6f2ec', '#f6f2ec', { gown: true, long: 2, dk: 0.06 });
const GROOM = look(22, 1, 0, '#1a1824', '#1a1824', { bow: true });
const GUESTS = [look(20, 1, 4, '#e87aa8', '#e87aa8', { skirt: '#e87aa8', long: 2 }), look(21, 2, 1, '#8a8aa0', '#5a5a70')];
const DRIVER = look(21, 3, 0, '#1a1824', '#1a1824', { cap: true });
const doorOpen = t => Math.max(env(t, WED.open, WED.close, 2), env(t, 229, 237, 1.5));
function keys(t, K) {                                             // piecewise-linear path through [t, x, fy] keys
  if (t <= K[0][0]) return K[0];
  for (let i = 1; i < K.length; i++) if (t <= K[i][0]) { const [t0, x0, y0] = K[i - 1], [t1, x1, y1] = K[i], f = (t - t0) / (t1 - t0); return [t, lerp(x0, x1, f), lerp(y0, y1, f)]; }
  return K[K.length - 1];
}
const PATH_G = [[90, CH.cx - 4, 211], [94, CH.cx - 4, 211], [98, CH.cx - 4, 220], [106, CH.cx - 4, 232], [113, 334, 246]];
const PATH_B = [[90, CH.cx + 4, 211], [94, CH.cx + 4, 211], [98, CH.cx + 4, 220], [106, CH.cx + 4, 232], [113, 341, 246]];
const PATH_D = [[218, 322, 246], [226, CH.cx, 246], [230, CH.cx, 220], [233, CH.cx, 211]];
function drawWedding(t, crowd) {
  if (crowd <= 0) return;
  const fr = step(t, STRIDE) & 1;
  // the guests: out at the doors, confetti, cheering and waving the car off, then back in
  if (t >= WED.open + 2 && t < WED.close - 4) GUESTS.forEach((P, k) => {
    const side = k ? 1 : -1, gx = CX + side * 16, inT = WED.open + 2, backT = 132;
    let x = gx, a = 1, o = { view: 1 };
    if (t < inT + 3) { x = lerp(CX, gx, (t - inT) / 3); a = clamp((t - inT) / 1.2, 0, 1); }
    else if (t >= backT) { x = lerp(gx, CX, clamp((t - backT) / 4, 0, 1)); o.view = 0; a = 1 - clamp((t - backT - 3) / 2, 0, 1); }
    if (t >= 92 && t < 106) { const th = (step(t, 200) + k) & 1; o.aL = th ? 1 : 4; o.aR = th ? 4 : 1; }
    else if (t >= 112 && t < 130) { o.aR = 1; o.aL = (step(t, 480) + k) & 1 ? 1 : 0; }
    put(211, () => { ctx.globalAlpha = a; person(x, 211, P, o); ctx.globalAlpha = 1; });
  });
  // the couple
  if (t >= WED.out && t < WED.seated) for (const [P, path, k] of [[GROOM, PATH_G, 0], [BRIDE, PATH_B, 1]]) {
    const [, x, fy] = keys(t, path), a = clamp((t - WED.out) / 1.5, 0, 1);
    let o = { view: 1 };
    if (t >= 106 && t < WED.kiss) o = { view: 2, dir: -1, step: fr };
    else if (t >= WED.kiss) o = { view: 2, dir: k ? -1 : 1, lean: k ? -1 : 1 };
    else if (t >= 94) o.step = 0;
    if (k === 1 && t < WED.kiss) o.aR = 4;                          // her bouquet
    const sink = t > WED.seated - 1.5 ? (t - (WED.seated - 1.5)) * 5 : 0;
    put(fy, () => {
      ctx.globalAlpha = a; const hy = person(x, fy + sink, P, o); ctx.globalAlpha = 1;
      if (k === 1 && t < WED.kiss) { R(Math.round(x) + 3, hy + 6, 3, 2, '#ff7ab0'); R(Math.round(x) + 4, hy + 5, 1, 1, '#fff2f6'); }
    });
  }
  if (t >= WED.kiss + 0.5 && t < WED.kiss + 5.5) {               // a little heart rising over the kiss
    const f = (t - WED.kiss - 0.5) / 5, hx = 337, hy = 216 - Math.round(f * 14);
    ctx.globalAlpha = 1 - f;
    ['.p.p.', 'ppppp', '.ppp.', '..p..'].forEach((row, j) => { for (let i = 0; i < 5; i++) if (row[i] === 'p') R(hx - 2 + i, hy + j, 1, 1, '#ff7ab0'); });
    ctx.globalAlpha = 1;
  }
  // the driver: out of the car, along the walk, up the carpet and in
  if (t >= WED.driver[0] && t < WED.driver[1]) {
    const [, x, fy] = keys(t, PATH_D), a = t < 219 ? t - 218 : 1 - clamp((t - 233) / 2, 0, 1);
    const o = t < 226 ? { view: 2, dir: 1, step: fr } : { view: 0, step: fr };
    put(fy, () => { ctx.globalAlpha = clamp(a, 0, 1); person(x, fy, DRIVER, o); ctx.globalAlpha = 1; });
  }
}
const CONFETTI = [];
{ const r = rng(77); for (let k = 0; k < 64; k++) CONFETTI.push({ t0: 92 + k * 0.2 + r() * 0.15, g: k & 1, vx: 4 + r() * 9, vy: 15 + r() * 10, land: 213 + r() * 12, col: ['#ff8ac8', '#fff4d0', '#8ad8ff', '#ffd25a', '#c8a0ff'][Math.floor(r() * 5)], ph: r() * 6 }); }
function drawConfetti(t, crowd) {
  if (crowd <= 0 || t < 92 || t >= 140) return;
  const fade = 1 - smooth(130, 140, t);
  for (const c of CONFETTI) {
    const s = t - c.t0;
    if (s < 0) continue;
    const gx = CX + (c.g ? 16 : -16), dir = c.g ? -1 : 1;
    let x = gx + dir * c.vx * s + Math.sin(s * 5 + c.ph) * 1.2, y = 197 - c.vy * s + 12 * s * s;
    if (y > c.land) { const sl = (c.vy + Math.sqrt(c.vy * c.vy + 48 * (c.land - 197))) / 24; x = gx + dir * c.vx * sl + Math.sin(sl * 5 + c.ph) * 1.2; y = c.land; }
    ctx.globalAlpha = fade; R(Math.round(x), Math.round(y), 1, 1, c.col);
  }
  ctx.globalAlpha = 1;
}

/* ================= Our walk: travertine paving that catches the lamps, a mosaic medallion, a bed of flowers ================= */
const VP = { x: 240, y: 118 };                                   // the paving's vanishing point
const MED = { x: 222, y: 242, rx: 60, ry: 11 };                  // the medallion set in the paving
const POOLS = [                                                  // [cx, cy, rx, ry, colour, strength]
  [LAMPS[0], 216, 56, 15, '#ffc070', 0.9], [LAMPS[1], 216, 56, 15, '#ffc070', 0.9], [NEAR_LAMP.x + 18, 214, 50, 10, '#ffc070', 0.6],
  [EF.x, 213, 80, 9, '#ffd27a', 0.4], [NEAR_LAMP.x, 250, 78, 22, '#ffc070', 0.95], [CH.cx, 228, 60, 24, '#ffb460', 0.75],
  [378, 222, 32, 12, '#ff5aa0', 0.35], [SHOWGIRL.x, 236, 30, 8, '#ffd27a', 0.3],
];
function lit(i, y, col) {
  for (const [cx, cy, rx, ry, lc, s] of POOLS) {
    const d = Math.hypot((i - cx) / rx, (y - cy) / ry);
    if (d >= 1) continue;
    const q = Math.floor((1 - d) * s * 5 + bayer(i, y)) / 5;
    if (q > 0) col = tint(col, lc, Math.min(0.66, q * 0.8));
  }
  return col;
}
function renderFloor() {
  const p = painter(W, H);
  for (let y = FLOOR; y < H; y++) for (let i = 0; i < W; i++) {
    const dz = y - VP.y, v = 900 / dz, u = (i - VP.x) / dz * 9;
    const fv = frac(v / 1.6), fu = frac(u / 1.2), tile = (Math.floor(v / 1.6) * 7 + Math.floor(u / 1.2) * 3) & 3;
    let col = ['#4a3c38', '#52433c', '#4e3f3a', '#56463e'][tile];
    if (hash(i >> 1, y, 5) < 0.06) col = tint(col, '#6a5648', 0.5);                    // the stone's own veining
    if (fv < 0.07 || fu < 0.06 * 88 / dz) col = '#30262a';                           // joints
    if (y <= FLOOR + 4) col = y === FLOOR + 4 ? '#7a6454' : (i + y) % 6 === 0 ? '#2a2026' : '#3a2e30';   // a border band along the rail
    const m = Math.hypot((i - MED.x) / MED.rx, (y - MED.y) / MED.ry);                 // the medallion: rings and an eight-point star
    if (m < 1) {
      const a = Math.atan2((y - MED.y) / MED.ry, (i - MED.x) / MED.rx), ray = Math.floor((a / TAU + 1) * 16) & 1;
      if (m > 0.9) col = '#8a7058'; else if (m > 0.85) col = '#2e2428'; else if (m > 0.8) col = '#7a6250';
      else if (m < 0.22) col = m > 0.16 ? '#2e2428' : '#a0845e';
      else col = ray ? '#5e4a3c' : '#3e322e';
    }
    p.put(i, y, lit(i, y, col));
  }
  return p.done();
}
function renderFlowers() {                                       // round shrubs in bloom along the front, cut by the frame
  const p = painter(W, H), BLOOM = ['#ff6aa8', '#f6e6f0', '#ffcf5a', '#ff8a52'];
  for (let k = 0; k < 13; k++) {
    const cx = 6 + k * 22 + Math.round(hash(k, 1, 71) * 6), cy = 268, rx = 12 + hash(k, 2, 71) * 4, ry = 9 + hash(k, 3, 71) * 3;
    const bloom = BLOOM[k % 4];
    for (let y = Math.floor(cy - ry); y < H; y++) for (let i = Math.floor(cx - rx); i <= cx + rx; i++) {
      const d = Math.hypot((i - cx) / rx, (y - cy) / ry);
      if (d > 1 || i > 286) continue;
      let col = d > 0.8 && y < cy ? '#2e5a3a' : (i + y) % 5 === 0 ? '#1a3424' : '#22432e';
      for (let m = 0; m < 4; m++) {
        const fx = cx - rx * 0.6 + m * rx * 0.4 + (hash(k, m, 73) - 0.5) * 2, fy = cy - ry * 0.55 + (m & 1) * 2;
        const e = Math.hypot(i - fx, (y - fy) * 1.2);
        if (e < 1.8) col = e < 0.7 ? '#fff4f8' : (i + y) & 1 ? bloom : tint(bloom, '#ffffff', 0.3);
      }
      p.put(i, y, lit(i, y, col));
    }
  }
  return p.done();
}

/* ================= Palms lining the walk ================= */
const PALMS = [{ x: 340, foot: 207, top: 80, lean: -8 }, { x: 474, foot: 200, top: 64, lean: 4 }];
function renderPalm(P) {
  const p = painter(W, H), put = p.put, n = P.foot - P.top;
  for (let k = 0; k <= n; k++) {                                   // a ringed trunk, bending a little
    const f = k / n, x = Math.round(P.x + P.lean * f * f), y = P.foot - k, w = f < 0.2 ? 4 : 3;
    for (let j = 0; j < w; j++) put(x - 1 + j, y, k % 4 === 0 ? '#2e2026' : j === 0 ? '#7a5a46' : j === w - 1 ? '#3a2a2a' : '#5a4236');
  }
  const cx = P.x + P.lean, cy = P.top;
  for (let f = 0; f < 9; f++) {                                     // nine fronds, drooping
    const a = -Math.PI / 2 + (f - 4) * 0.42 + (f & 1) * 0.08, L = 22 + (f % 3) * 4, droop = 0.9 + (Math.abs(f - 4) * 0.12);
    for (let s = 0; s <= 1; s += 0.025) {
      const x = cx + Math.cos(a) * L * s * 1.25, y = cy + Math.sin(a) * L * s * 0.7 + droop * L * s * s * 0.9;
      put(x, y, '#2e5a3a');
      const lf = Math.round(4 * (1 - s) + 1);
      for (let q = 1; q <= lf; q++) { put(x - q * 0.3, y + q, q === lf ? '#16301e' : '#1e4228'); put(x + q * 0.3, y + q, '#1e4228'); }
    }
  }
  for (const [dx, dy] of [[-1, 1], [1, 1], [0, 2], [-2, 2], [2, 2]]) put(cx + dx, cy + dy, '#8a5a2a');   // dates under the crown
  return p.done();
}

/* ================= The little wedding chapel (the cozy corner) ================= */
const { x0: C0, x1: C1, cx: CX, eave: EAVE, base: CB } = CH;
const APEX = 118, BELF = { top: 100, bot: 121 }, SPIRE = 66;
const CWIN = [[391, 403], [445, 457]];                          // arched windows [x0, x1], rows 158..190
const gableY = i => Math.round(EAVE - (EAVE - APEX) * (1 - Math.abs(i - CX) / (CX - C0 + 4)));
function renderChapel() {
  const p = painter(W, H), put = p.put;
  const board = (i, y) => {                                         // white clapboard: moonlit high, lamplit low
    const warm = clamp((y - 165) / 45, 0, 1) > bayer(i, y);
    if ((y - EAVE) % 3 === 2) return warm ? '#d2b8a4' : '#b6b0c4';
    return warm ? '#f8ece0' : '#e6e4f0';
  };
  // walls, corner boards
  for (let y = EAVE; y < CB; y++) for (let i = C0; i <= C1; i++) {
    let col = board(i, y);
    if (i <= C0 + 1) col = i === C0 ? '#fffaf2' : '#ece6ee';
    if (i >= C1 - 1) col = i === C1 ? '#9a94aa' : '#dcd6e2';
    put(i, y, col);
  }
  // the gable: clapboard in the triangle, the shingled roof edge, white barge boards
  for (let i = C0 - 4; i <= C1 + 4; i++) {
    const g = gableY(i);
    for (let y = g + 2; y < EAVE; y++) if (i > C0 && i < C1) put(i, y, board(i, y));
    put(i, g - 1, '#3a3448'); put(i, g - 2, '#4a4258');
    put(i, g, '#fffaf2'); put(i, g + 1, '#c8c0d0');
  }
  for (let i = C0 - 4; i <= C1 + 4; i++) { put(i, EAVE, '#fffaf2'); put(i, EAVE + 1, '#9a92a6'); }
  // steeple: belfry with its bell, a cornice, the white spire and a little gold heart on top
  for (let y = BELF.top; y <= BELF.bot; y++) for (let i = CX - 7; i <= CX + 7; i++) {
    let col = i <= CX - 6 ? '#fffaf2' : i >= CX + 6 ? '#aaa2b8' : '#ecebf2';
    const ay = y - (BELF.top + 4), ah = Math.abs(i - CX);
    if (y >= BELF.top + 4 && y <= BELF.bot - 4 && ah <= 3 && !(ay < 2 && ah > 1 + ay)) col = '#2a2034';   // arched opening
    put(i, y, col);
  }
  for (let y = BELF.top + 7; y <= BELF.top + 10; y++) for (let i = CX - 2; i <= CX + 2; i++) if (Math.abs(i - CX) <= 1 + (y - BELF.top - 7) / 2) put(i, y, i < CX ? '#ffe08a' : '#c8902e');
  put(CX, BELF.top + 11, '#8a6420');
  for (let i = CX - 9; i <= CX + 9; i++) { put(i, BELF.top - 1, '#fffaf2'); put(i, BELF.top, '#bdb6c8'); }
  for (let y = SPIRE; y < BELF.top - 1; y++) {
    const hw = Math.round(8 * (y - SPIRE) / (BELF.top - 1 - SPIRE));
    for (let i = CX - hw; i <= CX + hw; i++) put(i, y, i < CX ? ((y & 3) === 0 ? '#dcd8e6' : '#f6f4fa') : ((y & 3) === 0 ? '#9690a8' : '#c2bcd0'));
  }
  ['.r.r.', 'rrrrr', '.rrr.', '..r..'].forEach((row, j) => { for (let i = 0; i < 5; i++) if (row[i] === 'r') put(CX - 2 + i, SPIRE - 5 + j, j === 0 || i === 0 ? '#ffe08a' : '#d8a238'); });
  put(CX, SPIRE - 1, '#c8902e');
  // a heart-shaped window glowing in the gable
  ['.ooo.ooo.', 'oHhpopppo', 'ohppppppo', 'opppppppo', '.opppppo.', '..opppo..', '...opo...', '....o....'].forEach((row, j) => {
    for (let i = 0; i < 9; i++) { const col = { o: '#b83a5e', H: '#fff0f4', h: '#ffd0dc', p: '#ff9ab8' }[row[i]]; if (col) put(CX - 4 + i, 130 + j, col); }
  });
  // arched windows with lace curtains, flower boxes
  for (const [a, b] of CWIN) {
    for (let y = 157; y <= 191; y++) for (let i = a - 1; i <= b + 1; i++) {
      const top = y < 162 && Math.abs(i - (a + b) / 2) > (b - a) / 2 + 1 - (y - 156) * 1.4;
      if (top) continue;
      const edge = i === a - 1 || i === b + 1 || y === 157 || y === 191 || (y < 162 && Math.abs(i - (a + b) / 2) > (b - a) / 2 - (y - 157) * 1.4);
      let col = edge ? '#fffaf2' : y < 172 ? '#ffe2a0' : '#f6bd6a';
      if (!edge && (i === (a + b) >> 1 || y === 174)) col = '#e8d8c8';                        // mullions
      if (!edge && (i - a < 2 || b - i < 2) && (y + i) % 2 === 0) col = '#fff6ec';             // lace
      put(i, y, col);
    }
    for (let i = a - 2; i <= b + 2; i++) { put(i, 192, '#7a4a3a'); put(i, 193, '#5a3226'); put(i, 191, hash(i, 1, 81) < 0.5 ? '#ff7ab0' : '#3a7a44'); }
  }
  // the portico: pediment, two columns, the double door glowing behind its glass
  for (let y = 168; y <= 177; y++) for (let i = CX - 15; i <= CX + 15; i++) {
    const hw = 15 - (177 - y) * 1.6;
    if (Math.abs(i - CX) > hw) continue;
    put(i, y, Math.abs(i - CX) > hw - 1.5 || y === 177 ? '#fffaf2' : '#e2dcea');
  }
  for (const cx of [CX - 12, CX + 11]) for (let y = 178; y < CB; y++) {
    put(cx, y, '#fffaf2'); put(cx + 1, y, y < 181 || y > CB - 3 ? '#fffaf2' : '#bdb6c8');
  }
  for (let y = 183; y < CB; y++) for (let i = CX - 6; i <= CX + 6; i++) {
    if (y < 186 && Math.abs(i - CX) > 3 + (y - 183) * 1.5) continue;
    const edge = Math.abs(i - CX) === 6 || i === CX || (y < 186 && Math.abs(i - CX) > 2 + (y - 183) * 1.5);
    let col = edge ? '#d8ccc0' : y < 199 ? (y < 191 ? '#ffe6aa' : '#ffd07a') : '#f2eae2';
    if (!edge && y >= 199 && (i === CX - 3 || i === CX + 3 || y === 204)) col = '#cfc4bc';
    put(i, y, col);
  }
  put(CX - 2, 200, '#c8902e'); put(CX + 2, 200, '#c8902e');
  // the porch and its steps, the red carpet running out to us, stanchions with a velvet rope
  for (let y = CB; y <= CB + 9; y++) {
    const ext = y >= CB + 6 ? 4 : y >= CB + 3 ? 2 : 0;
    for (let i = C0 - 6 - ext; i <= C1 + 6 + ext; i++) put(i, y, (y - CB) % 3 === 0 ? '#fbf4e8' : (y - CB) % 3 === 1 ? '#d6ccbe' : '#9a8e88');
  }
  for (let y = CB; y < H; y++) {
    const hw = Math.round(6 + (y - CB) * 0.13);
    for (let i = CX - hw; i <= CX + hw; i++) put(i, y, Math.abs(i - CX) === hw ? '#e0405a' : (y > CB + 9 && (i + y) % 7 === 0) ? '#9a1828' : '#b81e32');
  }
  for (const side of [-1, 1]) for (let k = 0; k < 3; k++) {
    const y = 228 + k * 14, hw = Math.round(6 + (y - CB) * 0.13) + 5, x = CX + side * hw;
    for (let j = 0; j < 7; j++) put(x, y - j, j === 6 ? '#fff0b0' : j === 0 ? '#8a6420' : '#e0b040');
    if (k < 2) { const y2 = y + 14, x2 = CX + side * (Math.round(6 + (y2 - CB) * 0.13) + 5); for (let s = 1; s < 12; s++) { const f = s / 12; put(lerp(x, x2, f), lerp(y - 5, y2 - 5, f) + Math.round(3 * Math.sin(Math.PI * f)), '#8a1a3a'); } }
  }
  // potted palms beside the door
  for (const px0 of [CX - 21, CX + 20]) {
    for (let y = CB - 7; y < CB; y++) for (let i = -3; i <= 3; i++) if (Math.abs(i) <= 3 - (y > CB - 3 ? 1 : 0)) put(px0 + i, y, i < 0 ? '#c8704a' : '#8e4a32');
    for (let f = 0; f < 7; f++) {
      const a = -Math.PI / 2 + (f - 3) * 0.5;
      for (let s = 1; s <= 9; s++) put(px0 + Math.cos(a) * s, CB - 8 + Math.sin(a) * s * 0.9 + (s * s) * 0.05 * Math.abs(f - 3), s > 6 ? '#3a8a4a' : '#2a6a3a');
    }
  }
  const c = p.done();
  // the neon sign over the portico, on a dark board (the letters are lit per frame)
  rect(p.x, CX - 20, 160, 41, 9, '#24142c'); rect(p.x, CX - 20, 160, 41, 1, '#4a2a4a');
  return c;
}
const NEON_HEART = { x: 377, y: 106 };                           // the heart-and-arrow sign on its pole, left of the chapel
function renderNeon() {
  const p = painter(W, H), put = p.put, { x: hx, y: hy } = NEON_HEART;
  for (let y = hy + 28; y < CB; y++) { put(hx, y, '#3a3044'); put(hx + 1, y, '#1a1420'); }
  for (let i = hx - 14; i <= hx + 14; i++) for (let y = hy + 13; y <= hy + 27; y++) put(i, y, Math.abs(i - hx) === 14 || y === hy + 13 || y === hy + 27 ? '#4a3a52' : '#1c1224');
  return p.done();
}
function heartAt(x, y) { const X = x / 10.5, Y = -(y - 1.5) / 9.5; return (X * X + Y * Y - 1) ** 3 - X * X * Y * Y * Y; }
function drawNeon(t) {
  const { x: hx, y: hy } = NEON_HEART, on = 0.92 + 0.08 * wave(t, 600);
  for (let y = -10; y <= 11; y++) for (let x = -12; x <= 12; x++) {                    // the heart's tube
    const f = heartAt(x, y), g = heartAt(x, y + 1), e = heartAt(x + 1, y);
    if (f <= 0 && (g > 0 || e > 0 || heartAt(x - 1, y) > 0 || heartAt(x, y - 1) > 0)) px(ctx, hx + x, hy + y, '#ff4f9a');
    else if (f <= 0 && f > -0.06) px(ctx, hx + x, hy + y, '#ffc0dc');
  }
  for (let k = -13; k <= 12; k++) px(ctx, hx + k, hy + 2 - Math.round(k * 0.45), '#ffe08a');   // the arrow through it
  for (const [dx, dy] of [[13, -4], [12, -5], [12, -3], [11, -4], [13, -5]]) px(ctx, hx + dx, hy + 2 + dy, '#fff4c0');   // its head
  for (const [dx, dy] of [[-13, 6], [-14, 5], [-12, 7], [-14, 7], [-13, 8]]) px(ctx, hx + dx, hy + 2 + dy, '#ffd25a');   // feathers
  halo(HL.neonPink, hx, hy, 0.8 * on);
  sign(ctx, 'OPEN', hx - 8, hy + 15, '#7af0ff');
  sign(ctx, '24 HRS', hx - 11, hy + 21, '#ff8ac8');
  sign(ctx, 'WEDDINGS', CX - Math.floor(signWidth('WEDDINGS') / 2), 162, '#ff9ad0');
  halo(HL.neonSmall, CX, 164, 0.7 * on);
}
// the JUST MARRIED convertible, heading off along the walk with cans on strings
const CAR = { x: 294, y: 236 };
function renderCar() {
  const w = 64, h = 22, p = painter(w + 14, h), put = p.put;
  const bodyTop = i => i < 10 ? 9 - Math.floor(i / 4) : i < 48 ? 7 : i < 57 ? 7 - Math.floor((i - 48) / 3) : 4;    // hood, cockpit, rear fin
  for (let i = 1; i < w - 2; i++) {
    const t0 = bodyTop(i);
    for (let y = t0; y <= 16; y++) {
      let col = y === t0 ? '#ffd8e6' : y < 8 ? '#f4a6c4' : y < 15 ? '#d0658e' : '#a8486e';
      if (y === 15) col = '#e8eef8';                                 // chrome strip
      put(i, y, col);
    }
  }
  for (let i = 0; i < 4; i++) { put(i, 12, '#e8eef8'); put(i, 13, '#c8d0e0'); put(w - 3 + i, 13, '#e8eef8'); }   // bumpers
  put(1, 11, '#fff6c8'); put(w - 3, 10, '#ff3a3a');                 // headlight, tail light
  for (let y = 1; y <= 6; y++) put(18 + Math.floor((6 - y) / 2), y, y < 3 ? '#e8eef8' : '#a8d4f0');   // windshield
  for (let i = 21; i < 47; i++) put(i, 6, '#5a1420');                                                  // the cockpit's rim
  for (const [a, b] of [[29, 34], [39, 45]]) for (let i = a; i <= b; i++) for (let y = 3; y <= 5; y++)     // two rounded seat backs
    if (!(y === 3 && (i === a || i === b))) put(i, y, y === 3 ? '#d04a5a' : '#a82838');
  for (const [dx, dy] of [[23, 3], [24, 3], [22, 4], [25, 4], [23, 5], [24, 5]]) put(dx, dy, '#1a1418');   // steering wheel
  for (let y = 8; y <= 14; y++) put(25, y, '#a8486e');                                                  // door seam
  for (const wx of [12, 50]) for (let i = wx - 5; i <= wx + 5; i++) { put(i, 13, '#3a1a2a'); put(i, 14, '#3a1a2a'); }   // wheel arches
  for (const wx of [12, 50]) for (let y = 13; y <= 21; y++) for (let i = wx - 4; i <= wx + 4; i++) {
    const d = Math.hypot(i - wx, y - 17);
    if (d < 4.6) put(i, y, d < 1.4 ? '#c8d0e0' : d < 2.6 ? '#f2f2ee' : '#16141c');
  }
  for (const [cx, cy] of [[w + 4, 18], [w + 8, 19], [w + 12, 18]]) {
    for (let y = cy; y < cy + 3; y++) for (let i = cx; i < cx + 2; i++) put(i, y, i === cx ? '#d0d4dc' : '#8a8e9a');
    for (let s = 0; s < 4; s++) put(lerp(w - 2, cx, s / 4), lerp(13, cy, s / 4), '#c8c4cc');
  }
  const c = p.done();
  sign(p.x, 'JUST MARRIED', 7, 8, '#fff4f8');
  return c;
}
// a bench on the walk, seen from behind: slatted backrest, cast-iron ends (its sitters are drawn with the people)
function renderBench() {
  const w = 26, p = painter(w, 22), put = p.put, b = 21;          // local rows: 21 = the floor under its feet
  for (let i = 1; i < w - 1; i++) for (let y = b - 1; y <= b; y++) put(i, y, '#120c14', 110);   // shadow
  for (let y = b - 12; y <= b - 6; y++) for (let i = 1; i < w - 1; i++) {
    const slat = (y - (b - 12)) % 3;
    put(i, y, slat === 2 ? '#2a1a14' : slat === 0 ? '#9a6a3e' : '#7a4e2c');
  }
  for (let i = 1; i < w - 1; i++) put(i, b - 5, '#5a3a22');
  for (const ex of [0, w - 2]) for (let y = b - 13; y <= b; y++) { put(ex, y, '#1a1418'); put(ex + 1, y, y < b - 10 ? '#3a3440' : '#1a1418'); }
  return p.done();
}
let MIRROR;                                                       // scratch canvas for the glossy floor
function drawFloorShine() {                                       // polished stone: a faint mirror of the rail and the lake
  const span = 46, [sc, sx] = MIRROR;
  sx.globalCompositeOperation = 'copy';
  sx.drawImage(ctx.canvas, 0, FLOOR - span, W, span, 0, 0, W, span);
  for (let k = 0; k < span; k++) {
    ctx.globalAlpha = 0.24 * (1 - k / span) ** 1.6;
    ctx.drawImage(sc, 0, span - 1 - k, W, 1, 0, FLOOR + k, W, 1);
  }
  ctx.globalAlpha = 1;
}
// the near lamp: fluted base, iron shaft with a collar, scrolled arms, five globes (lit per frame)
const NL_GLOBES = [[0, -7], [-8, 0], [8, 0], [-4, -3], [4, -3]].map(([dx, dy]) => [NEAR_LAMP.x + dx, NEAR_LAMP.head + dy]);
function renderNearLamp() {
  const p = painter(W, H), put = p.put, { x, foot, head } = NEAR_LAMP;
  for (let y = head + 4; y <= foot; y++) {
    const w = y > foot - 4 ? 9 : y > foot - 12 ? 5 : y > head + 38 && y < head + 42 ? 5 : 3, x0 = x - (w >> 1);
    for (let k = 0; k < w; k++) put(x0 + k, y, k === 0 ? '#4a4256' : k === w - 1 ? '#0c0a10' : '#1c1822');
  }
  for (let k = -9; k <= 9; k++) { const yy = head + 4 + Math.round(k * k / 26); put(x + k, yy, '#1c1822'); put(x + k, yy + 1, '#3a3446'); }   // arms
  for (const sx of [-1, 1]) for (let k = 0; k < 4; k++) put(x + sx * (2 + k), head + 6 + (k > 1 ? 1 : 0), '#1c1822');
  return p.done();
}
function drawNearLamp(t) {
  ctx.drawImage(NL_C, 0, 0);
  for (const [gx, gy] of NL_GLOBES) {
    rect(ctx, gx - 2, gy - 2, 5, 4, '#fff4d2'); rect(ctx, gx - 1, gy - 3, 3, 1, '#ffe6a6'); rect(ctx, gx - 1, gy + 2, 3, 1, '#f2c27a');
    px(ctx, gx + 2, gy + 1, '#d89a58'); px(ctx, gx - 2, gy + 1, '#f2c27a'); rect(ctx, gx - 1, gy + 3, 3, 1, '#1c1822');
  }
  halo(HL.lampBig, NEAR_LAMP.x, NEAR_LAMP.head - 2, 0.95); halo(HL.lampCore, NEAR_LAMP.x, NEAR_LAMP.head - 2, 0.8);
  drawMoths(t);
}
const CAT = ['k...k', 'kk.kk', 'kkkkk', '.kyk.', 'kkkk.', 'kkkkk', '.kkkt', '....t'];
function renderVignette() {
  const [c, x] = makeCanvas(W, H), img = x.createImageData(W, H), d = img.data;
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    const v = smooth(0.78, 1.45, Math.hypot((i - W / 2) / (W / 2), (y - H / 2) / (H / 2) * 0.9));
    const q = Math.floor(v * 4 + bayer(i, y)) / 4;
    if (q <= 0) continue;
    const o = (y * W + i) * 4; d[o] = 4; d[o + 1] = 3; d[o + 2] = 14; d[o + 3] = Math.round(q * 0.42 * 255);
  }
  x.putImageData(img, 0, 0);
  return c;
}
/* ================= The High Roller's colours: its LED rim changes now and then, with slow cross-fades ================= */
const HR_COLS = [['#ff5cbc', '#a8307e', '#ff4fb4'], ['#5ad8ff', '#2a6aa8', '#4ab8ff'], ['#b88aff', '#5a3a9a', '#a06cff'], ['#ff8a4a', '#a83a2a', '#ff7a3a']];
const HR_PROG = [[0, 38], [1, 42, 98], [2, 102, 150], [3, 154, 206], [0, 210, 240]];
function hrMix(t) {                                              // [colour, next colour, how far it has faded over]
  if (t < 38) return [0, 0, 0];
  for (let i = 1; i < HR_PROG.length; i++) {
    const [k, a, b] = HR_PROG[i], prev = HR_PROG[i - 1][0];
    if (t < a) return [prev, k, smooth(a - 4, a, t)];
    if (t < b) return [k, k, 0];
  }
  return [0, 0, 0];
}
function renderRim(k) {
  const { x: cx, y: cy, r: R } = HRW, p = painter(W, H), [c1, c2] = HR_COLS[k];
  for (let a = 0; a < 720; a++) {
    const t = a / 720 * TAU, s = Math.sin(t), c = Math.cos(t);
    p.put(cx + s * R, cy - c * R, c1); p.put(cx + s * (R - 1), cy - c * (R - 1), c2); p.put(cx + s * (R - 2), cy - c * (R - 2), '#3a1a46');
  }
  return p.done();
}
function drawWheel(t) {
  const [a, b, u] = hrMix(t);
  halo(HL['hr' + a], HRW.x, HRW.y, 0.9 * (1 - u)); if (u > 0) halo(HL['hr' + b], HRW.x, HRW.y, 0.9 * u);
  ctx.drawImage(WHEEL_C, 0, 0);
  ctx.drawImage(RIM_C[a], 0, 0);
  if (u > 0) { ctx.globalAlpha = u; ctx.drawImage(RIM_C[b], 0, 0); ctx.globalAlpha = 1; }
  drawCabins(t);
  return HR_COLS[u > 0.5 ? b : a][2];
}

/* ================= The Eiffel Tower's sparkle: white lights twinkling over the gold, twice a loop ================= */
const SPARKLE = [[68, 80], [214, 226]];
const SPARK_PTS = [];
{ const r = rng(53); while (SPARK_PTS.length < 90) { const h = 2 + r() * 114, hw = efHW(h); SPARK_PTS.push([Math.round(EF.x + (r() * 2 - 1) * hw * 0.92), Math.round(EF.base - h)]); } }
function drawSparkle(t) {
  const e = Math.max(...SPARKLE.map(([a, b]) => env(t, a, b, 2)));
  if (e <= 0) return;
  const s = step(t, 1920);
  SPARK_PTS.forEach(([x, y], k) => { if (hash(k, s, 57) < 0.16 * e) { px(ctx, x, y, '#ffffff'); if (hash(k, s, 58) < 0.25) halo(HL.spark, x, y, 0.8 * e); } });
}

/* ================= Passing in the sky: a tour helicopter over the Strip, a plane letting down towards the airport ================= */
const HELI = { t0: 4, t1: 38 }, PLANE = { t0: 100, t1: 150 };
function drawSkyPassers(t) {
  if (t >= HELI.t0 && t < HELI.t1) {
    const f = (t - HELI.t0) / (HELI.t1 - HELI.t0), x = Math.round(lerp(-12, 250, f)), y = Math.round(112 + 3 * Math.sin(f * 5));
    R(x - 2, y, 5, 2, '#0e0c16'); R(x + 3, y + 1, 4, 1, '#0e0c16'); R(x - 3, y - 1, 7, 1, step(t, 2400) & 1 ? '#3a3648' : '#1a1824');
    px(ctx, x, y + 2, '#0e0c16');
    if (frac(t / 1.2) < 0.35) { px(ctx, x + 6, y + 1, '#ff3a3a'); halo(HL.red, x + 6, y + 1, 0.8); }
    px(ctx, x - 2, y + 1, '#fff2c0'); halo(HL.heliLamp, x - 2, y + 1, 0.5);
  }
  if (t >= PLANE.t0 && t < PLANE.t1) {
    const f = (t - PLANE.t0) / (PLANE.t1 - PLANE.t0), x = Math.round(lerp(496, -16, f)), y = Math.round(lerp(58, 74, f));
    R(x - 3, y, 7, 1, '#2a2638'); px(ctx, x - 4, y, '#fff6dc'); halo(HL.heliLamp, x - 4, y, 0.7);
    px(ctx, x + 1, y - 1, '#2a2638'); px(ctx, x + 3, y - 1, '#2a2638');
    if (frac(t / 1.5) < 0.3) { px(ctx, x, y + 1, '#ff3a3a'); px(ctx, x + 1, y + 1, '#ffffff'); }
  }
}

/* ================= The JUST MARRIED convertible: it drives the newly-weds off, and the chapel's driver brings it back ================= */
function carX(t) {
  const [d0, d1] = WED.drive, [b0, b1] = WED.back;
  if (t >= d0 && t < d1) { const f = (t - d0) / (d1 - d0); return CAR.x - 420 * f * f; }
  if (t >= d1 && t < b0) return null;
  if (t >= b0 && t < b1) { const f = 1 - (t - b0) / (b1 - b0); return CAR.x - 420 * f * f; }
  return CAR.x;
}
function drawCar(t, crowd) {
  const x = crowd > 0 ? carX(t) : CAR.x;                         // nobody to drive it at crowd 0: it stays parked
  if (x === null || x < -80) return;
  const X = Math.round(x), moving = X !== CAR.x, Y = CAR.y;
  put(Y + 21, () => {
    ctx.globalAlpha = 0.5; R(X + 3, Y + 20, 58, 2, '#0a0610'); ctx.globalAlpha = 1;
    ctx.drawImage(CAR_C, 0, 0, 64, 22, X, Y, 64, 22);
    ctx.drawImage(CAR_C, 64, 0, 14, 22, X + 64, Y - (moving && (step(t, 960) & 1) ? 1 : 0), 14, 22);   // the cans rattle along
    if (moving) for (const wx of [12, 50]) { const sp = step(t, 1440) & 1; R(X + wx - 1, Y + 16 + sp, 3, 1, '#9aa2b4'); R(X + wx - 1 + sp * 2, Y + 15, 1, 3, '#9aa2b4'); }
    const head = (hx, hair, skin, top, extra) => { R(X + hx, Y + 4, 4, 3, top); R(X + hx + 1, Y + 1, 2, 3, skin); R(X + hx + 1, Y + 1, 2, 1, hair); if (extra) extra(); };
    if (crowd > 0 && t >= WED.seated && t < WED.drive[1]) {
      head(26, GROOM.hair, GROOM.skin, '#1a1824');
      head(31, BRIDE.hair, BRIDE.skin, '#f6f2ec', () => {
        R(X + 34, Y + 1, 1, 4, '#f6f2ec'); if (moving) R(X + 35, Y + 2, 2, 1, '#f6f2ec');      // her veil, blowing back
        if (t > WED.seated + 2 && frac(t / 1.6) < 0.6) { R(X + 31, Y - 2, 1, 4, BRIDE.skin); }  // she waves
      });
    }
    if (crowd > 0 && t >= WED.back[0] && t < WED.driver[0]) head(26, DRIVER.hair, DRIVER.skin, '#1a1824', () => { R(X + 26, Y, 4, 1, '#14121c'); });
  });
}

/* ================= Traffic on the Strip ================= */
const CARS = [];
{ const r = rng(19); for (let i = 0; i < 16; i++) CARS.push({ dir: i % 2 ? -1 : 1, n: 4 + Math.floor(r() * 5), ph: r(), cool: r() < 0.3 }); }
function drawTraffic(t) {
  for (const c of CARS) {
    const u = frac(t * c.n / LOOP + c.ph), x = c.dir > 0 ? Math.round(-6 + u * 400) : Math.round(394 - u * 400), y = c.dir > 0 ? 161 : 160;
    rect(ctx, x - 1, y, 3, 1, '#0c0a12');
    px(ctx, x + 2 * c.dir, y, c.cool ? '#e6f0ff' : '#fff0c0');
    px(ctx, x - 2 * c.dir, y, '#ff3a2c');
  }
}

/* ================= Windows that switch on and off on the far towers ================= */
const TOGGLES = [];
{
  const r = rng(61);
  for (let k = 0; k < 40; k++) {                                   // on the Bellagio's near face
    const i = 1 + 3 * Math.floor(r() * 9) + (r() < 0.5 ? 0 : 1), fl = 2 + Math.floor(r() * 24);
    const y = Math.round(BEL.foot - (fl + 0.5) * 3 * (1 - 0.32 * Math.pow(i / BEL.x1, 1.3)));
    if (y > belTop(i) + 3) TOGGLES.push({ x: i, y, n: 2 + Math.floor(r() * 5), off: r(), col: WIN_W[Math.floor(r() * 3)], dark: '#3a3046' });
  }
  for (let k = 0; k < 10; k++) TOGGLES.push({ x: 64 + 2 * Math.floor(r() * 11), y: 99 + 3 * Math.floor(r() * 16), n: 2 + Math.floor(r() * 5), off: r(), col: WIN_W[Math.floor(r() * 3)], dark: '#2a2a40' });
}

/* ================= Weather: a clear desert night of its own; a picked weather falls at three depths ================= */
const WEATHER = weatherPlans([]);
const WX_LAND = [[HZ - 4, HZ + 2], [FLOOR + 2, 236], [H + 4, H + 4]];
const WX_LIGHTS = [
  ...LAMPS.map(x => ({ x, y: 168, rx: 14, ry: 22 })), { x: NEAR_LAMP.x, y: NEAR_LAMP.head, rx: 22, ry: 34 },
  { x: CH.cx, y: 196, rx: 16, ry: 16, col: '#ffcf8a' }, { x: NEON_HEART.x, y: NEON_HEART.y, rx: 18, ry: 16, col: '#ff7ab0' },
  { x: EF.x, y: 110, rx: 24, ry: 60, col: '#ffd27a' },
];
const WX_LEAVES = { wind: [0, 60, W, 200] };                       // no tree overhangs the walk: the wind carries leaves over the lake
const WX = { land: WX_LAND, lights: WX_LIGHTS, leaves: WX_LEAVES };
const WX_BB = { land: WX_LAND, leaves: WX_LEAVES, lights: [...WX_LIGHTS, { x: DIA.x, y: DIA.y, rx: 34, ry: 22, col: '#ffe08a' }] };

/* ================= Pre-render everything static ================= */
let HL, SKY_C, MOON_C, CLOUDS, MOUNT_C, FAR_C, WHEEL_C, RIM_C, EARTH_F, MOONPIC_C, PUMPKIN_C, SHORE_C, BB_C, BB_AD, PITCH, EIFFEL_C,
  BALLOON_C, WATER_C, MIST_C, BALUSTRADE_C, FLOOR_C, FLOWERS_C, PALM_C, CHAPEL_C, NEON_C, CAR_C, CAT_C, BENCH_C, NL_C, VIG_C;
function prerender() {
  HL = {
    moon: makeHalo(40, '#b8a8ff', 0.2, 1.8), moonCore: makeHalo(14, '#fff4dc', 0.35, 1.5), city: makeHalo(170, '#ff7a5a', 0.06, 1.3),
    efLow: makeHalo(58, '#ffb04a', 0.16, 1.6), efHigh: makeHalo(34, '#ffd27a', 0.14, 1.6), beacon: makeHalo(8, '#ffffff', 0.6, 1.3),
    sph_earth: makeHalo(34, '#4a8cff', 0.18, 1.6), sph_moon: makeHalo(34, '#d8d8f0', 0.16, 1.6), sph_pumpkin: makeHalo(34, '#ff8a2a', 0.2, 1.6),
    balloon: makeHalo(20, '#5a8aff', 0.2), bbWash: makeHalo(44, '#ffc870', 0.07, 1.5), star: makeHalo(10, '#ff5a3a', 0.3),
    red: makeHalo(4, '#ff3030', 0.6, 1), spark: makeHalo(4, '#ffffff', 0.5, 1.2), heliLamp: makeHalo(5, '#fff2c0', 0.5, 1.2),
    globe: makeHalo(7, '#ffe6a8', 0.5, 1.3), lampWide: makeHalo(36, '#ffb060', 0.14, 1.6), phone: makeHalo(5, '#bfe0ff', 0.5),
    neonPink: makeHalo(26, '#ff4f9a', 0.22, 1.5), neonSmall: makeHalo(22, '#ff7ac0', 0.16), chapelWin: makeHalo(16, '#ffb45a', 0.2),
    door: makeHalo(24, '#ffb460', 0.24), doorOpen: makeHalo(40, '#ffc878', 0.22, 1.5), heartWin: makeHalo(10, '#ff7aa8', 0.3),
    lampBig: makeHalo(54, '#ffb060', 0.22, 1.7), lampCore: makeHalo(14, '#ffe2a8', 0.45, 1.4),
    fountain: makeHalo(150, '#9ab8ff', 0.08, 1.4), nozzle: makeHalo(12, '#bfe0ff', 0.28), jetTop: makeHalo(20, '#eef4ff', 0.3),
  };
  HR_COLS.forEach(([, , g], k) => { HL['hr' + k] = makeHalo(42, g, 0.07, 1.6); });
  SKY_C = renderSky(); MOON_C = renderMoon(); CLOUDS = CLOUD_DEFS.map(makeCloudLayer); MOUNT_C = renderMountains();
  FAR_C = renderFar(); WHEEL_C = renderWheel(); RIM_C = HR_COLS.map((c, k) => renderRim(k));
  EARTH_F = renderEarthFrames(60, -1.75, 0.4);
  MOONPIC_C = renderMoonPic(); PUMPKIN_C = [0, 1, 2].map(renderPumpkin);
  SHORE_C = renderShore(); BB_C = renderBillboard(); BB_AD = renderBillboardAd(); PITCH = makePitch(BB.w, BB.h, { accent: '#39ff14' });
  EIFFEL_C = renderEiffel(); BALLOON_C = renderBalloon();
  WATER_C = renderWater(); MIST_C = makeMist(); BALUSTRADE_C = renderBalustrade(); FLOOR_C = renderFloor(); FLOWERS_C = renderFlowers();
  PALM_C = PALMS.map(renderPalm); CHAPEL_C = renderChapel(); NEON_C = renderNeon(); CAR_C = renderCar();
  BENCH_C = renderBench(); NL_C = renderNearLamp(); MIRROR = makeCanvas(W, 64);
  CAT_C = sprite(CAT, { k: '#1a1418', y: '#ffd25a', t: '#1a1418' });
  [FC, fx] = makeCanvas(W, FB + 1);
  SHOOT_C = renderColumn(7, 100, '#ffffff', '#f0f6ff', '#c6dcff', '#a6ccff', 0.22); JG_C = renderJetGlow(); JET_C = renderJets(); RH = new Float32Array(RIP.length);
  VIG_C = renderVignette();
}

/* ================= Frame compositor (back to front) ================= */
function drawScroll(cv, y, k, t) {
  const off = Math.floor(frac(k * t / LOOP) * W);
  ctx.drawImage(cv, off - W, y); ctx.drawImage(cv, off, y);
}
const GABLE_BULBS = [];
for (let i = C0 - 3; i <= C1 + 3; i += 3) GABLE_BULBS.push([i, gableY(i) - 1]);
function drawChapel(t, crowd) {
  ctx.drawImage(CHAPEL_C, 0, 0);
  const op = crowd > 0 ? doorOpen(t) : 0;
  if (op > 0) {                                                    // the doors open on the lit aisle
    ctx.globalAlpha = op;
    R(CX - 5, 187, 11, 23, '#ffe4a6'); R(CX - 5, 187, 11, 3, '#fff2cc'); R(CX - 1, 196, 3, 14, '#c8303a');
    for (const y of [198, 202, 206]) { R(CX - 5, y, 3, 2, '#8a5a32'); R(CX + 3, y, 3, 2, '#8a5a32'); }
    ctx.globalAlpha = 1;
    halo(HL.doorOpen, CX, 214, op);
  }
  for (const [a, b] of CWIN) halo(HL.chapelWin, (a + b) / 2, 174, 0.85 + 0.1 * wave(t, 400, a / 50));
  halo(HL.door, CX, 196, 0.9); halo(HL.heartWin, CX, 134, 0.85);
  const s = step(t, 480);
  GABLE_BULBS.forEach(([x, y], k) => px(ctx, x, y, (k + s) % 3 === 0 ? '#ff8ac8' : '#fff0c0'));
  ctx.drawImage(NEON_C, 0, 0);
  drawNeon(t);
  if (crowd >= 0.5) drawChapelFigure(t);
  const cx = CX + 14, flick = frac(t / 7.5) < 0.12;                // the chapel's cat on the step, its tail flicking
  ctx.drawImage(CAT_C, cx, CB + 1);
  if (flick) { px(ctx, cx + 4, CB + 7, '#1a1418'); px(ctx, cx + 5, CB + 6, '#1a1418'); }
}
const MOTHS = [[0, 3.2, 7, 5], [1, 4.8, 9, 6], [2, 6.0, 6, 8]];   // [id, seconds a circle, rx, ry]
function drawMoths(t) {
  for (const [id, per, rx, ry] of MOTHS) {
    const a = TAU * t / (240 / Math.round(240 / per)) + id * 2.1, wob = hash(id, step(t, 960), 33) < 0.5 ? 0 : 1;
    px(ctx, Math.round(NEAR_LAMP.x + Math.cos(a) * rx), Math.round(NEAR_LAMP.head - 2 + Math.sin(a * 2) * ry) + wob, '#e8dcc0');
  }
}
const WIN_WALK = [[30, 38], [150, 158], [222, 230]];                // someone crossing the chapel behind its left window
function drawChapelFigure(t) {
  const w = WIN_WALK.find(([a, b]) => t >= a && t < b);
  if (!w) return;
  const [a0, b0] = CWIN[0], u = (t - w[0]) / (w[1] - w[0]), x = Math.round(lerp(a0 - 3, b0 + 3, u));
  ctx.fillStyle = '#8a5a3a';
  for (let y = 166; y < 191; y++) for (let i = x - 2; i <= x + 2; i++) {
    if (i < a0 || i > b0 || (y < 170 && Math.abs(i - x) > 1)) continue;
    ctx.globalAlpha = y < 170 ? 0.7 : 0.55; ctx.fillRect(i, y, 1, 1);
  }
  ctx.globalAlpha = 1;
}
function drawLampFloor(t) {                                        // the lamps' light running across the polished stone
  for (const lx of LAMPS) streak(ctx, t, lx, 3, '#ffcf8a', 0.22, lx, FLOOR + 1, FLOOR + 24);
  streak(ctx, t, NEAR_LAMP.x, 5, '#ffcf8a', 0.18, 99, FLOOR + 1, FLOOR + 40);
}
function renderFrame(t, { pitch = false, billboard = true, weather, reducedMotion = false, crowd: share } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  const crowd = share ?? CROWD;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const wx = WEATHER(weather), wxc = billboard ? WX_BB : WX;
  // sky, stars, moon, thin clouds, the plane, the Strip's glow, the mountain
  ctx.drawImage(SKY_C, 0, 0);
  ctx.fillStyle = '#f2ecff';
  for (const s of STARS) { ctx.globalAlpha = s.b * (0.55 + 0.45 * wave(t, s.k, s.ph)); ctx.fillRect(s.x, s.y, 1, 1); }
  ctx.globalAlpha = 1;
  halo(HL.moon, MOON.x, MOON.y); halo(HL.moonCore, MOON.x, MOON.y);
  ctx.drawImage(MOON_C, MOON.x - MOON.r, MOON.y - MOON.r);
  for (const L of CLOUDS) drawScroll(L.c, L.y, L.k, t);
  halo(HL.city, 236, 176, 1);
  ctx.drawImage(MOUNT_C, 0, 0);
  // the far towers (windows switching), the Strat's beacon, the Sphere's picture, the High Roller
  ctx.drawImage(FAR_C, 0, 0);
  for (const w of TOGGLES) px(ctx, w.x, w.y, hash(w.x * 7 + w.y, Math.floor(frac(t / LOOP + w.off) * w.n), 3) < 0.55 ? w.col : w.dark);
  const sb = frac(t / 2) < 0.3; px(ctx, STRAT.x, STRAT.tip, sb ? '#ff4040' : '#5a1a1a'); if (sb) halo(HL.red, STRAT.x, STRAT.tip, 0.9);
  const pics = drawSphere(t), hrCol = drawWheel(t);
  drawSkyPassers(t);
  // the far shore, the billboard, the Eiffel Tower and its sparkle, the balloon, the Strip's traffic
  ctx.drawImage(SHORE_C, 0, 0);
  if (billboard) drawBillboard(t, pitch);
  halo(HL.efLow, EF.x, 136, 0.95); halo(HL.efHigh, EF.x, 84, 0.8);
  ctx.drawImage(EIFFEL_C, 0, 0);
  drawSparkle(t);
  px(ctx, EF.x, EF.tip, '#ffffff'); halo(HL.beacon, EF.x, EF.tip, 0.6 + 0.4 * wave(t, 160));
  halo(HL.balloon, BAL.x, BAL.top + 10, 0.9);
  ctx.drawImage(BALLOON_C, 0, 0);
  drawMarquee(t);
  drawTraffic(t);
  drawWeather(ctx, wx.shared, 0, t, wxc, reducedMotion);
  // the lake: its reflections, the fountains and their mist
  drawLake(t, billboard, pics, hrCol);
  const tops = drawFountain(t);
  drawFountainLayer(t, tops);
  drawMist(t);
  // our balustrade, its lamps, the people at the rail, the palm by the rail
  ctx.drawImage(BALUSTRADE_C, 0, 0);
  drawLampHeads();
  drawRailers(t, crowd);
  ctx.drawImage(PALM_C[0], 0, 0);
  // our walk: the polished paving, the chapel, the strollers, the wedding, the car, the near lamp, the flowers
  ctx.drawImage(FLOOR_C, 0, FLOOR, W, H - FLOOR, 0, FLOOR, W, H - FLOOR);
  drawFloorShine();
  drawLampFloor(t);
  ctx.drawImage(PALM_C[1], 0, 0);
  drawChapel(t, crowd);
  drawWeather(ctx, wx.shared, 1, t, wxc, reducedMotion);
  drawWalkers(t, crowd);
  drawBench(t, crowd);
  drawShowgirl(t, crowd);
  drawWedding(t, crowd);
  drawCar(t, crowd);
  put(NEAR_LAMP.foot, () => drawNearLamp(t));
  flushDL();
  drawConfetti(t, crowd);
  ctx.drawImage(FLOWERS_C, 0, 0);
  drawWeather(ctx, wx.shared, 2, t, wxc, reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

export default {
  id: 'las-vegas',
  name: 'Las Vegas',
  tagline: 'Fountains on the Strip',
  timeZone: 'America/Los_Angeles',
  accent: '#39ff14',
  focusX: 284,
  landmark: [259, 309],                                // the Eiffel Tower of Paris Las Vegas, foot to foot
  landmarkTop: 30,                                     // its beacon
  billboard: BB,
  crowd: CROWD,                                        // the share of the crowd shown by default; render(t, { crowd }) takes any 0..1
  spots: [                                             // what answers a tap (site/js/ui/spots.js)
    { kind: 'landmark', x: 266, y: 30, w: 37, h: 118 },                         // the Eiffel Tower
    { kind: 'water', x: 214, y: 168, w: 110, h: 14 },                           // the Bellagio lake, in front of the tower
    { kind: 'neon', x: NEON_HEART.x - 13, y: NEON_HEART.y - 10, w: 27, h: 22 },  // the chapel's heart-and-arrow sign
    { kind: 'bell', x: CH.cx - 4, y: 104, w: 9, h: 13 },                        // the chapel's bell
  ],
  ambience: { water: 0.45, traffic: 0.3, crowd: 0.22, wind: 0.12 },
  events: [
    { t: 44.2, type: 'splash', x: 74, gain: 0.5 }, { t: 47.1, type: 'splash', x: 154, gain: 0.5 }, { t: 51.4, type: 'splash', x: 196, gain: 0.8 },
    { t: 90.5, type: 'bell', x: 424, gain: 0.6 }, { t: 93, type: 'bell', x: 424, gain: 0.5 },
    { t: 114.6, type: 'chime', x: 337, gain: 0.5 }, { t: 124.4, type: 'horn', x: 320, gain: 0.35 }, { t: 125.2, type: 'horn', x: 300, gain: 0.3 },
    { t: 184.2, type: 'splash', x: 74, gain: 0.5 }, { t: 187.1, type: 'splash', x: 154, gain: 0.5 }, { t: 191.4, type: 'splash', x: 196, gain: 0.8 },
    { t: 213, type: 'horn', x: 220, gain: 0.25 },
  ],
  create(c) {
    ctx = c;
    prerender();
    return { render: renderFrame };
  },
};
